import { DialogComponent } from "@theme/dialog";
import { formatCurrency, fetchConfig } from "@theme/utilities";
import { CartUpdateEvent, CartGroupedSections, ThemeEvents } from "@theme/events";
import { markExperienceUsed, prepareExperienceAdd, trackExperience } from "@theme/experience-tracking";

// Fallbacks only — the real values come from the section's `min_extras`/
// `max_extras` settings (sections/charm-builder.liquid), so a merchant
// can adjust them without a code change. These match the schema's own
// defaults (min 1 / max 5, confirmed 2026-09-08).
const DEFAULT_MIN_EXTRAS = 1;

// Stack & Save — the store-wide automatic quantity discount the cart
// drawer's progress bar describes (snippets/cart-drawer-rewards.liquid,
// snippets/cart-drawer-redesign-summary.liquid): 2 eligible pieces = 10%
// off, 3+ = 15% off, every piece counting once per unit and products
// tagged `no-stack-save` excluded. Mirrored here so the builder can show
// the same bar and the discounted prices before anything reaches the
// cart. Shopify applies the real discount at checkout; these numbers are
// a preview of it.
const STACK_TIERS = [
  { pieces: 3, rate: 0.15 },
  { pieces: 2, rate: 0.1 },
];
const STACK_EXCLUDE_TAG = "no-stack-save";

/** Price in cents after a Stack & Save rate, rounded the way Shopify rounds line discounts. */
const discounted = (cents, rate) => Math.round(cents * (1 - rate));

/**
 * "$45.00" or "<s>$50.00</s> $45.00" when a rate applies.
 * @param {number} cents
 * @param {number} rate
 */
const priceHtml = (cents, rate) =>
  rate > 0
    ? `<s class="charm-builder__price-was">${formatCurrency(cents)}</s> <span class="charm-builder__price-now">${formatCurrency(discounted(cents, rate))}</span>`
    : formatCurrency(cents);
const DEFAULT_MAX_EXTRAS = 5;

const HTML_ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/**
 * Product name as shown in the builder. Droplet titles in this catalog all
 * end in " - Crystal Charm", which is redundant inside a charm builder and
 * eats card space (user request) — the cart/PDP keep the full title.
 * @param {string | undefined} title
 */
const displayTitle = (title) => String(title ?? "").replace(/\s*[-–—]\s*Crystal Charm\s*$/i, "");

/**
 * Length label as shown in the builder: Signature's variants are titled
 * "14-16 inch" while the other chains use 17.5" — normalise to the inch
 * mark so the size pills read consistently (user request).
 * @param {string | undefined} label
 */
const displayLength = (label) => String(label ?? "").replace(/\s*inch(es)?\s*$/i, '"');

/** @param {unknown} value */
const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
// The picture editor edits a charm's placement on one necklace as its own
// field, "offset_x@<necklace product id>".
function placedEditorFields(component) {
  const { necklace_placement: placement, ...fields } = component ?? {};
  for (const [chainId, values] of Object.entries(placement ?? {}))
    for (const [name, value] of Object.entries(values ?? {}))
      if (Number.isFinite(value)) fields[`${name}@${chainId}`] = value;
  return fields;
}

/**
 * Generates a per-build identifier stamped on every cart line the builder
 * adds (as line-item property `_bundle_instance_id`), so the Cart
 * Transform Function can group this specific build's lines together
 * without merging two different customers' — or two different builds by
 * the same customer's — necklaces into one bundle by mistake.
 */
/**
 * Same Shopify CDN image at a different rendered width. The catalog's
 * URLs come out of sections/charm-builder.liquid at one size
 * (`image_url: width: 1200`); the preview chain is drawn far larger
 * than that — the framed, zoomed square is ~950-1600 CSS px on desktop,
 * double that on a retina screen — so at 1200px it went visibly soft
 * (client, 2026-09-18: "looks a bit blurry"). The source PNGs are
 * 2380px; the CDN caps a larger request at the source size. Cards
 * only need ~400px. Leaves a URL with no width param alone.
 * @param {string} url
 * @param {number} width
 */
function imageAtWidth(url, width) {
  if (!url) return url;
  return url.replace(/([?&])width=\d+/, `$1width=${width}`);
}

function generateBundleInstanceId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  // Fallback for browsers without crypto.randomUUID (rare, but this theme
  // targets a public storefront so it can't assume a modern browser).
  return `cb-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Where the opaque pixels of a builder image sit inside its (square)
 * box, as fractions of the box: `left`/`top`/`width`/`height` of the
 * visible shape, `cx` its horizontal centre, `aspect` the image's own
 * width/height. Null when the image couldn't be read.
 * @typedef {{ left: number, top: number, width: number, height: number, cx: number, aspect: number, stoneHeight: number, stoneWidth: number }} ImageGeometry
 */

/** @type {Map<string, Promise<ImageGeometry | null>>} */
const imageGeometryCache = new Map();

/**
 * Measures a builder image's opaque outline in the browser (downscaled
 * onto a small canvas, alpha channel only). The metaobject's stored
 * `content_width` used to carry this, but it was measured once off the
 * *previous* set of photos: when the client re-shot every charm
 * (2026-09-16) those numbers stayed behind — droplets stored at ~0.39
 * wide now measure ~0.26, and the Moon / Boundaries Babe charms became
 * lariat shots (a long drop chain above the charm) measuring ~0.21 —
 * so spacing and vertical placement were off on every single charm.
 * Measuring the live image means a re-upload can never desync again.
 * Needs the CDN's `Access-Control-Allow-Origin: *` (present on
 * cdn.shopify.com) — a tainted canvas throws on getImageData, which is
 * caught and reported as null so callers fall back to the stored value.
 * @param {string} url
 * @returns {Promise<ImageGeometry | null>}
 */
function measureImageGeometry(url) {
  if (!url) return Promise.resolve(null);
  const cached = imageGeometryCache.get(url);
  if (cached) return cached;
  const pending = new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const SAMPLE = 384;
        const canvas = document.createElement("canvas");
        canvas.width = SAMPLE;
        canvas.height = SAMPLE;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) return resolve(null);
        context.drawImage(image, 0, 0, SAMPLE, SAMPLE);
        const { data } = context.getImageData(0, 0, SAMPLE, SAMPLE);
        let minX = SAMPLE;
        let minY = SAMPLE;
        let maxX = -1;
        let maxY = -1;
        const rowWidths = new Uint16Array(SAMPLE);
        for (let y = 0; y < SAMPLE; y++) {
          for (let x = 0; x < SAMPLE; x++) {
            // > 128, i.e. at least half-opaque: anti-aliased fringe and
            // soft drop shadows otherwise pad the box out past the shape
            // the eye sees. 20 wasn't enough — the gold spiral's PNG has
            // a faint glow reaching ~6% of the image above its bail
            // (alpha 20-100), which put the measured "bail" that far
            // above the real one, so the charm hung with its bail
            // floating under the ring instead of wrapped round it
            // (client, 2026-09-17: "spiral charm is not located
            // correctly").
            if (data[(y * SAMPLE + x) * 4 + 3] > 128) {
              rowWidths[y]++;
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX < 0) return resolve(null);
        // The crystal body is the longest broad, opaque run below the
        // thin wire loop. Measure it separately: the CSV describes the
        // crystal, while height_cm includes its metal fitting.
        const stoneWidth = Math.max(...rowWidths);
        let stoneHeight = 0;
        let run = 0;
        for (const width of rowWidths) {
          run = width > stoneWidth * 0.45 ? run + 1 : 0;
          stoneHeight = Math.max(stoneHeight, run);
        }
        resolve({
          left: minX / SAMPLE,
          top: minY / SAMPLE,
          width: (maxX - minX + 1) / SAMPLE,
          height: (maxY - minY + 1) / SAMPLE,
          cx: (minX + maxX + 1) / 2 / SAMPLE,
          aspect: image.naturalWidth / image.naturalHeight || 1,
          stoneHeight: stoneHeight / SAMPLE,
          stoneWidth: stoneWidth / SAMPLE,
        });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
  imageGeometryCache.set(url, pending);
  return pending;
}

/**
 * Extends DialogComponent (not plain Component) so the builder itself is
 * the modal — its own root `<dialog ref="dialog">` (sections/
 * charm-builder.liquid) needs no separate wrapping `<dialog-component>`.
 * That matters here specifically because a wrapping `<dialog-component>`
 * is itself a Component instance, and every ref inside it (the grids,
 * the preview, the section <details>) would then belong to *that*
 * component's `this.refs`, not this class's — the same pattern
 * cart-drawer.js/quick-add.js use for their own root dialogs.
 */
class CharmBuilder extends DialogComponent {
  requiredRefs = [
    "dialog",
    "catalogData",
    "chainGrid",
    "dropletGrid",
    "charmGrid",
    "previewMain",
    "reviewButton",
    "sectionChain",
    "sectionDroplet",
    "sectionCharm",
    "reviewDialog",
  ];

  /** @type {{ chains: CatalogEntry[], components: (CatalogEntry & {group: 'droplet'|'charm'})[] } | null} */
  static #catalog = null;

  /** @type {Map<string, any>} cache of fetched /products/{handle}.js responses, keyed by handle */
  #productCache = new Map();

  /**
   * Each selected item remembers the metal it was added under, so
   * flipping the Gold/Silver toggle afterwards re-filters the *grid*
   * (spec §5) without retroactively changing which variant an
   * already-chosen item resolves to — a customer who picked a Gold droplet,
   * then switches the toggle to Silver to browse, shouldn't come back to
   * find their earlier pick silently became the Silver variant.
   *
   * A chain selection also pins the exact `variantId` — every chain
   * product has a Length option on top of Material, and the chain grid
   * lists each length as its own card (client: "all sizes and colours of
   * chains listed as separate products"), so the customer's choice is a
   * specific variant, not just a product + metal that #resolveVariant
   * would otherwise collapse to the shortest available length.
   * @type {{ chain: {handle: string, metal: 'gold'|'silver', variantId: number} | null, extras: {handle: string, metal: 'gold'|'silver', quantity: number, previewLayers: number[]}[] }}
   */
  selection = { chain: null, extras: [] };

  // Each physical charm gets its own layer when added, including duplicates.
  #nextPreviewLayer = 0;
  #startTracked = false;
  #useTracked = false;

  /**
   * All three toggle groups' "Gold" button starts marked `is-active` in
   * the markup (sections/charm-builder.liquid), so this must default to
   * "gold" for all three — a mismatched default here would render a grid
   * filtered to silver while its toggle still visibly shows Gold
   * selected, until the customer clicks a toggle themselves.
   * @type {{ chain: 'gold' | 'silver', droplet: 'gold' | 'silver', charm: 'gold' | 'silver' }}
   */
  #metal = { chain: "gold", droplet: "gold", charm: "gold" };

  /**
   * Reads `min_extras`/`max_extras` from the section's own settings
   * (`data-min-extras`/`data-max-extras`, set by the merchant in the
   * theme editor) rather than a hardcoded constant, falling back to the
   * schema's own defaults if either is missing/non-numeric — e.g. an
   * older cached section render from before these settings existed.
   * Clamps `min` to never exceed `max` (a merchant could set them
   * backwards in the editor) rather than leaving the builder in a state
   * where "Review Your Design" can never enable.
   * @returns {{ min: number, max: number }}
   */
  #extrasLimits() {
    const parsedMin = parseInt(this.dataset.minExtras ?? "", 10);
    const parsedMax = parseInt(this.dataset.maxExtras ?? "", 10);
    const max = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : DEFAULT_MAX_EXTRAS;
    const min = Number.isFinite(parsedMin) && parsedMin > 0 ? parsedMin : DEFAULT_MIN_EXTRAS;
    return { min: Math.min(min, max), max };
  }

  /**
   * Desktop (per Figma) shows all three sections open at once with no
   * expand/collapse affordance at all; mobile is a single-open accordion.
   * Tied to the same 1024px breakpoint the row/column preview+panel
   * layout switches at (assets/charm-builder.css).
   */
  #desktopQuery = window.matchMedia("(min-width: 1024px)");

  /** Bound so disconnectedCallback can remove the exact listener added here. */
  #handleDesktopQueryChange = () => this.#syncAccordionForViewport();

  /** Bound so disconnectedCallback can remove the exact listener added here. */
  #handleChainGridScroll = () => this.#normaliseChainLoop();

  /**
   * The grids first render in connectedCallback, while the dialog is
   * still closed and everything inside it measures 0px wide — so whether
   * the chain row should loop (#renderGrid: "does one set overflow the
   * row?") can't be decided until it's actually on screen, and can
   * change again when the window is resized. Re-renders the chain row
   * whenever its width changes such that the loop decision would flip.
   * Attached in start() (the dialog opening is what gives the row a
   * width), not connectedCallback.
   */
  #chainGridResizeObserver = new ResizeObserver(() => this.#syncChainLoop());

  #headerResizeObserver = new ResizeObserver(() => this.#syncHeaderOffset());
  #sectionScrollFrame = 0;

  #syncHeaderOffset = () => {
    if (!this.refs.dialog.open) return;
    const headerBottom = document.querySelector(".header")?.getBoundingClientRect().bottom ?? 0;
    const offset = `${Math.max(headerBottom, 0)}px`;
    if (this.style.getPropertyValue("--charm-builder-header-offset") !== offset) {
      this.style.setProperty("--charm-builder-header-offset", offset);
    }
  };

  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute("data-calibration-app")) return;
    this.refs.chainGrid?.addEventListener("scroll", this.#handleChainGridScroll, { passive: true });
    this.#render();
    // Pre-existing gap, not caused by anything above: #updatePreview() was
    // only ever called from restart()/choose(), never on initial render —
    // a customer's very first look at the freshly-opened modal, before
    // touching anything, saw a blank preview box instead of the "Choose a
    // chain to begin your design" placeholder.
    this.#updatePreview();
    this.#syncAccordionForViewport();
    this.#desktopQuery.addEventListener("change", this.#handleDesktopQueryChange);
    // Both the navigation and announcement bar can change height when
    // switching between desktop and mobile with the builder still open.
    document.querySelectorAll(".header, .shopify-section-group-header-group").forEach((header) => {
      this.#headerResizeObserver.observe(header);
    });
    window.addEventListener("resize", this.#syncHeaderOffset, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktopQuery.removeEventListener("change", this.#handleDesktopQueryChange);
    this.refs.chainGrid?.removeEventListener("scroll", this.#handleChainGridScroll);
    this.#chainGridResizeObserver.disconnect();
    this.#headerResizeObserver.disconnect();
    window.removeEventListener("resize", this.#syncHeaderOffset);
    cancelAnimationFrame(this.#sectionScrollFrame);
  }

  #syncChainLoop() {
    const grid = this.refs.chainGrid;
    if (!grid || grid.clientWidth === 0) return;
    const looped = grid.dataset.cbLoop === "true";
    const cardCount = grid.querySelectorAll(".charm-builder__option").length;
    const shouldLoop = looped
      ? this.#chainSetWidth(grid) > grid.clientWidth && cardCount > 3
      : grid.scrollWidth > grid.clientWidth && cardCount > 1;
    if (shouldLoop !== looped) this.#renderGrid("chain");
  }

  /**
   * The chain row loops endlessly (client: "continuously append items to
   * the end, not rewind to the beginning"). #renderGrid lays the real
   * set of cards out three times — a clone set before, the real set, a
   * clone set after — and starts the scroll position on the real set.
   * Whenever scrolling carries the viewport fully into the trailing or
   * leading clone set, this jumps it back by exactly one set's width
   * with no animation: the clone sitting at that spot is pixel-identical
   * to the real card the viewport lands on, so the jump is invisible and
   * "›" can be pressed forever. The jump thresholds are a whole set
   * away from the starting position, so a one-card smooth scrollBy from
   * anywhere on the real set finishes before it can trip them.
   */
  #normaliseChainLoop() {
    const grid = this.refs.chainGrid;
    const setWidth = this.#chainSetWidth(grid);
    if (!setWidth) return;
    if (grid.scrollLeft >= setWidth * 2) {
      grid.scrollLeft -= setWidth;
    } else if (grid.scrollLeft < setWidth * 0.5) {
      grid.scrollLeft += setWidth;
    }
  }

  /**
   * Pixel width of one full set of chain cards (cards + gaps) inside a
   * looped chain grid, or 0 when the grid isn't currently looped (too
   * few cards to fill the row — see #renderGrid).
   * @param {HTMLElement} grid
   */
  #chainSetWidth(grid) {
    if (grid?.dataset.cbLoop !== "true") return 0;
    const firstReal = grid.querySelector('.charm-builder__option:not([data-cb-clone])');
    const firstClone = grid.querySelector(".charm-builder__option[data-cb-clone]");
    if (!firstReal || !firstClone) return 0;
    return firstReal.offsetLeft - firstClone.offsetLeft;
  }

  /** @returns {boolean} */
  #isMobileAccordion() {
    return !this.#desktopQuery.matches;
  }

  /**
   * Forces every section open on desktop, where the Figma design has no
   * collapse affordance at all — overrides whatever mobile-oriented
   * open/closed state `restart()`/`editSection()`/a user's own toggle
   * left the sections in. A no-op on mobile, where those methods' own
   * open/closed state (and `handleSectionToggle`'s exclusivity) stands.
   */
  #syncAccordionForViewport() {
    if (this.#isMobileAccordion()) return;
    this.refs.sectionChain.open = true;
    this.refs.sectionDroplet.open = true;
    this.refs.sectionCharm.open = true;
  }

  /**
   * Mobile: single-open accordion (per Figma) — opening any section
   * closes the other two. Desktop: sections have no collapse affordance
   * at all, so any close (a click/keypress on <summary>, which stays a
   * real focusable control for accessibility) is immediately reopened
   * here instead of being blocked at the CSS/pointer-events level.
   * Fires for both a customer's own interaction with a `<summary>`
   * (native `<details>` toggle) and this component's own
   * `ref.open = ...` assignments elsewhere (choose/restart/editSection),
   * since setting `.open` dispatches the same `toggle` event — so this
   * one handler covers every way a section's state can change, instead
   * of needing an explicit sync call at each call site.
   * @param {Event} event
   */
  handleSectionToggle(event) {
    const target = /** @type {HTMLDetailsElement} */ (event.target);
    if (!target) return;

    if (!this.#isMobileAccordion()) {
      if (!target.open) target.open = true;
      return;
    }

    if (!target.open) return;
    for (const ref of [this.refs.sectionChain, this.refs.sectionDroplet, this.refs.sectionCharm]) {
      if (ref !== target) ref.open = false;
    }
  }

  /**
   * Triggered by "Start Your Design" (blocks/charm-builder-start.liquid,
   * in the real PDP's buy-box column, via `on:click="#charm-builder/start"`
   * — an id-selector so it resolves regardless of that button living in a
   * different section than this element). Opens the builder as a modal
   * dialog (showDialog() is inherited from DialogComponent) rather than
   * scrolling to an embedded section.
   */
  start() {
    this.#recordStart();
    // The Stack & Save bar otherwise only renders after the first pick.
    this.#updateStackProgress();
    this.showDialog();
    // Measure after locking the page; keep this current on header resize.
    this.#syncHeaderOffset();
    // The chain row's loop can only be decided once the dialog has laid
    // out (see #syncChainLoop). A short timer is the primary trigger —
    // ResizeObserver callbacks are suppressed under the browser
    // automation used to test this theme, so the observer (kept for live
    // window resizes) couldn't be relied on alone.
    setTimeout(() => this.#syncChainLoop(), 50);
    this.#chainGridResizeObserver.observe(this.refs.chainGrid);
  }

  restart() {
    this.#startTracked = false;
    this.#useTracked = false;
    // Reached from "Clear Design" on the build screen and from "Restart
    // Customisations" inside the Review dialog — close that dialog if it's
    // open so the customer lands back on the (now empty) build screen
    // rather than an empty review.
    // Skip if it's already mid-close (addToCart closes it just before
    // calling restart): DialogComponent.closeDialog isn't idempotent
    // while its exit animation runs — a second call releases the shared
    // scroll lock twice and unlocks the page behind the cart drawer.
    const reviewDialogEl = this.refs.reviewDialog?.refs?.dialog;
    if (reviewDialogEl?.open && !reviewDialogEl.classList.contains("dialog-closing")) {
      this.refs.reviewDialog.closeDialog();
    }

    /** @type {typeof this.selection} */
    this.selection = { chain: null, extras: [] };
    // Reset the accordions back to their initial-page-load state — without
    // this, "Clear Design" wiped the selection but left whatever
    // open/closed state the customer's browsing had left the sections in
    // (e.g. chain collapsed, droplet expanded), so restarting looked like
    // nothing had happened.
    this.refs.sectionChain.open = true;
    this.refs.sectionDroplet.open = false;
    this.refs.sectionCharm.open = false;
    this.#syncAccordionForViewport();
    this.#updatePreview();
    this.#updateReviewButton();
    this.#renderAllGrids();
  }

  /**
   * Scrolls `target` into view within the build dialog's own scrollable
   * area (.dialog__inner.v-scrollable — see sections/charm-builder.liquid)
   * after the mobile accordion and refreshed cards have settled. Only
   * the dialog scrolls; its pinned preview/progress must clear the heading.
   * @param {HTMLElement} target
   */
  #scrollToSection(target) {
    if (this.#isMobileAccordion()) {
      // Native toggle events arrive later. Close siblings now so their
      // disappearing height cannot invalidate the scroll destination.
      for (const section of [this.refs.sectionChain, this.refs.sectionDroplet, this.refs.sectionCharm]) {
        section.open = section === target;
      }
    }
    cancelAnimationFrame(this.#sectionScrollFrame);
    this.#sectionScrollFrame = requestAnimationFrame(() => {
      if (!this.refs.dialog.open) return;
      const scroller = this.refs.dialog.querySelector(".dialog__inner");
      if (!scroller) return;
      const pinned = this.refs.dialog.querySelector(
        this.#isMobileAccordion() ? ".charm-builder__preview" : ".charm-builder__stack"
      );
      const heading = target.querySelector("summary") ?? target;
      const top = scroller.scrollTop + heading.getBoundingClientRect().top
        - scroller.getBoundingClientRect().top - (pinned?.getBoundingClientRect().height ?? 0);
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      scroller.scrollTo({ top, behavior: reduced ? "instant" : "smooth" });
    });
  }

  /**
   * One Gold/Silver choice drives all three sections' toggles at once —
   * clicking any of them updates every `#metal` entry and every toggle
   * button in the builder, not just the group clicked, so the three
   * swatches always show the same state (client: "can all the other
   * gold/silver swatches change too so everything is uniform while
   * selected"). Doesn't touch already-chosen items' own stored metal
   * (`this.selection.chain?.metal`/`extra.metal`) — this only changes
   * which metal each grid is currently *browsing*, per the existing
   * design (see the class field doc on `#metal`).
   * @param {Event} event
   */
  setMetal(event) {
    const button = event.target instanceof HTMLElement ? event.target.closest("[data-metal]") : null;
    if (!button) return;

    const metal = button.dataset.metal;
    if (metal !== "gold" && metal !== "silver") return;

    this.#metal.chain = metal;
    this.#metal.droplet = metal;
    this.#metal.charm = metal;

    for (const btn of this.querySelectorAll("[data-metal]")) {
      btn.classList.toggle("is-active", btn.dataset.metal === metal);
    }

    this.#renderGrid("chain");
    this.#renderGrid("droplet");
    this.#renderGrid("charm");
  }

  /**
   * Stack & Save state for the current selection: how many eligible
   * pieces are chosen and the discount rate that earns. Uses the product
   * cache (every selected product was fetched to draw its card) so it can
   * stay synchronous for card rendering.
   * @returns {{ pieces: number, rate: number }}
   */
  #stackState() {
    let pieces = 0;
    const count = (handle, quantity) => {
      const product = this.#productCache.get(handle);
      if (!product) return;
      if (Array.isArray(product.tags) && product.tags.includes(STACK_EXCLUDE_TAG)) return;
      pieces += quantity;
    };
    if (this.selection.chain) count(this.selection.chain.handle, 1);
    for (const extra of this.selection.extras) count(extra.handle, extra.quantity ?? 1);
    const tier = STACK_TIERS.find((t) => pieces >= t.pieces);
    return { pieces, rate: tier?.rate ?? 0 };
  }

  /** Rate to apply to one product (0 when it's excluded from Stack & Save). */
  #stackRateFor(product, rate) {
    if (Array.isArray(product?.tags) && product.tags.includes(STACK_EXCLUDE_TAG)) return 0;
    return rate;
  }

  /**
   * The Stack & Save bar at the top of the picker panel (sections/
   * charm-builder.liquid, ref="stackProgress") — same wording, segments
   * and labels as the cart drawer's own bar.
   */
  #updateStackProgress() {
    const el = this.refs.stackProgress;
    if (!el) return;
    const { pieces } = this.#stackState();
    let message;
    if (pieces >= 3) message = `✦ You've unlocked <strong>15% off!</strong>`;
    else if (pieces === 2) message = `✦ Add <strong>1 more piece</strong> to save <strong>15%</strong>`;
    else message = `✦ Add <strong>${2 - pieces} more ${pieces === 1 ? "piece" : "pieces"}</strong> to save <strong>10%!</strong>`;
    el.innerHTML = `
      <div class="charm-builder__stack-progress" aria-label="Stack and Save progress">
        <p class="charm-builder__stack-message">${message}</p>
        <div class="charm-builder__stack-track" aria-hidden="true">
          <span class="charm-builder__stack-segment${pieces >= 2 ? " is-complete" : ""}"></span>
          <span class="charm-builder__stack-segment${pieces >= 3 ? " is-complete" : ""}"></span>
        </div>
        <div class="charm-builder__stack-labels" aria-hidden="true"><span>10%</span><span>15%</span></div>
      </div>`;
  }

  /**
   * Total droplets + charms selected, counting quantity — the number the
   * builder's min/max rules and the "(x of 5 added)" count are about.
   * @returns {number}
   */
  #extrasTotal() {
    return this.selection.extras.reduce((sum, extra) => sum + (extra.quantity ?? 1), 0);
  }

  /**
   * `selection.extras` with each entry repeated `quantity` times, so the
   * preview, review list and cart payload — which all work per physical
   * piece (one hanging charm, one review card, one cart line labelled
   * "Custom Charm N") — need no quantity awareness of their own.
   * @returns {{ handle: string, metal: string | null, previewLayer: number }[]}
   */
  #expandedExtras() {
    return this.selection.extras.flatMap((extra) =>
      Array.from({ length: extra.quantity ?? 1 }, (_, index) => ({
        ...extra,
        previewLayer: extra.previewLayers[index],
      }))
    );
  }

  /**
   * "−"/"+" picker under a selected droplet/charm card (see #renderCard).
   * Steps that card's quantity within the builder's rules: never past the
   * shared max in total, and stepping down to 0 deselects the card.
   * @param {{ delta: number }} data - from the `?delta=` on:click suffix
   * @param {Event} event
   */
  adjustQuantity(data, event) {
    // Same reason as choose(): the grid re-render below detaches the
    // clicked button, which DialogComponent's click-outside check would
    // otherwise read as a click outside the builder and close it.
    event.stopPropagation();
    event.preventDefault();
    const button = event.target instanceof HTMLElement ? event.target.closest("button") : null;
    if (!button || button.disabled) return;
    const slot = button.closest(".charm-builder__option-slot");
    const key = slot instanceof HTMLElement ? slot.dataset.key : null;
    const delta = Number(data?.delta);
    if (!key || !Number.isFinite(delta) || delta === 0) return;

    const index = this.selection.extras.findIndex((extra) => (extra.key ?? extra.handle) === key);
    if (index < 0) return;
    const extra = this.selection.extras[index];
    const next = (extra.quantity ?? 1) + delta;

    if (next <= 0) {
      this.selection.extras.splice(index, 1);
    } else {
      if (delta > 0 && this.#extrasTotal() >= this.#extrasLimits().max) return;
      while (extra.previewLayers.length < next) extra.previewLayers.push(++this.#nextPreviewLayer);
      extra.previewLayers.length = next;
      extra.quantity = next;
    }

    this.#renderAllGrids();
    this.#updatePreview();
    this.#updateReviewButton();
  }

  /**
   * Makes `variantId` of chain `handle` the selected chain.
   * @param {string} handle
   * @param {number} variantId
   * @param {boolean} [advance] - open + scroll to the droplet section
   *   (a fresh chain pick); false when only the length of the already
   *   selected chain is being changed, where jumping away would fight
   *   the customer.
   */
  #selectChain(handle, variantId, advance = true) {
    const previousChainMetal = this.selection.chain?.metal;
    this.selection.chain = { handle, metal: this.#metal.chain, variantId };
    this.#recordUse();
    // Switching the necklace to a different metal empties the charms/
    // droplets already picked — they were chosen to go with the old
    // colour, and carrying them over onto a chain of the other metal
    // would leave a design that doesn't match (client: "remove all
    // charms IF the colour is switched"). Picking a *different chain
    // product in the same metal* (e.g. Curator Gold → Lockheart Gold)
    // is unaffected — nothing here to mismatch.
    if (previousChainMetal && previousChainMetal !== this.#metal.chain) {
      this.selection.extras = [];
    }
    if (!advance) return;
    // Chain is single-select: advance to droplets, keeping the mobile
    // accordion state settled before measuring the scroll destination.
    this.refs.sectionDroplet.open = true;
    this.#scrollToSection(this.refs.sectionDroplet);
  }

  /**
   * Length swatch under a chain card (see #renderCard). Selects that
   * chain at that length; sold-out lengths render disabled so never
   * reach here.
   * @param {{ variant: number }} data - from the `?variant=` on:click suffix
   * @param {Event} event
   */
  chooseChainSize(data, event) {
    // Same reason as choose(): the re-render detaches the clicked swatch.
    event.stopPropagation();
    event.preventDefault();
    const button = event.target instanceof HTMLElement ? event.target.closest("button") : null;
    if (!button || button.disabled) return;
    const slot = button.closest(".charm-builder__option-slot");
    const handle = slot instanceof HTMLElement ? slot.dataset.handle : null;
    const variantId = Number(data?.variant);
    if (!handle || !Number.isFinite(variantId)) return;

    const sameChain = this.selection.chain?.handle === handle;
    this.#selectChain(handle, variantId, !sameChain);

    this.#syncAccordionForViewport();
    this.#renderAllGrids();
    this.#updatePreview();
    this.#updateReviewButton();
  }

  /** @param {Event} event */
  choose(event) {
    const card = event.target instanceof HTMLElement ? event.target.closest(".charm-builder__option") : null;
    if (!card || card.getAttribute("aria-disabled") === "true") return;

    const group = card.dataset.cbGroup;
    const handle = card.dataset.handle;
    if (!group || !handle) return;
    const variantId = card.dataset.variantId ? Number(card.dataset.variantId) : null;

    // This builder is itself a DialogComponent (it extends DialogComponent
    // so its own root <dialog> needs no separate wrapping element — see
    // the class doc comment above) — DialogComponent's own click-outside-
    // to-close listener runs on this same element during the bubble
    // phase, *after* #renderAllGrids below has replaced `card`'s whole
    // grid (including `card` itself) via `grid.innerHTML = ...`. By the
    // time that listener runs, `event.target` (this card, or a child of
    // it) is a detached node, which its `dialog.contains(event.target)`
    // check reads as "clicked outside" — closing the dialog on every
    // selection. Stopping propagation here (choosing a card is fully
    // handled) keeps that check from ever seeing this event.
    event.stopPropagation();

    if (group === "chain") {
      // Chains now have their own Gold/Silver toggle too (matching
      // droplet/charm) — Signature Necklace's `variant_filter` still
      // pins it to Gold-waterproof regardless (its card simply doesn't
      // render at all while the Silver toggle is active, per
      // #renderGrid's metalMatches check below). The card's
      // data-variant-id is the length currently shown on it (the
      // selected one, or the first in stock) — chooseChainSize is how a
      // different length gets picked.
      if (variantId == null) return;
      this.#selectChain(handle, variantId);
    } else {
      const key = card.dataset.key || handle;
      const index = this.selection.extras.findIndex((extra) => (extra.key ?? extra.handle) === key);
      if (index >= 0) {
        // Clicking a selected card clears it entirely (whatever its
        // quantity); the "−"/"+" picker under the card (adjustQuantity)
        // is the way to step it up or down.
        this.selection.extras.splice(index, 1);
      } else {
        if (this.#extrasTotal() >= this.#extrasLimits().max) return;
        this.selection.extras.push({
          handle,
          key,
          metal: this.#metal[group],
          quantity: 1,
          previewLayers: [++this.#nextPreviewLayer],
        });
      }

      // Droplet/charm are multi-select, so only advance once the
      // customer has actually hit this section's own cap — advancing on
      // every click (like the chain does) would fight a customer who's
      // still browsing within the same section. Charm is the last
      // section, so there's nowhere further to advance to; the customer
      // uses "Review Your Design" instead once ready.
      if (group === "droplet" && this.#extrasTotal() >= this.#extrasLimits().max) {
        this.refs.sectionCharm.open = true;
        this.#scrollToSection(this.refs.sectionCharm);
      }
    }

    this.#recordUse();
    this.#syncAccordionForViewport();
    this.#renderAllGrids();
    this.#updatePreview();
    this.#updateReviewButton();
  }

  #recordStart() {
    if (this.#startTracked) return;
    this.#startTracked = true;
    void trackExperience("charm_builder_started");
  }

  #recordUse() {
    if (this.#useTracked) return;
    this.#recordStart();
    this.#useTracked = true;
    void markExperienceUsed("charm-builder");
    void trackExperience("charm_builder_used");
  }

  /**
   * "‹"/"›" buttons next to each section heading (sections/
   * charm-builder.liquid) — the chain grid is always a horizontal-scroll
   * row (mobile and desktop, per Figma); droplet/charm switch to a
   * horizontal-scroll row on mobile only and wrap into a multi-column
   * grid at the desktop breakpoint, where these buttons are hidden by
   * assets/charm-builder.css. Plain scroll math rather than a carousel
   * library, since the grids' contents get replaced wholesale on every
   * selection (#renderGrid) — a stateful carousel component would need
   * re-initializing on every click.
   *
   * These buttons live inside a <summary> (they sit next to the section
   * heading per Figma), so a click on them also reaches <summary>'s own
   * native "toggle the details" activation behaviour — without stopping
   * it, tapping ‹/› would also collapse/expand the section underneath.
   * preventDefault() cancels that default action while still letting the
   * click bubble to this component's own event-delegated listener.
   * @param {MouseEvent} event
   */
  scrollGrid(event) {
    const button = event.target instanceof HTMLElement ? event.target.closest("[data-cb-nav]") : null;
    if (!button) return;
    event.preventDefault();

    const grid = this.refs[/** @type {string} */ (button.dataset.cbNavTarget)];
    if (!grid) return;

    const cardWidth = grid.querySelector(".charm-builder__option")?.getBoundingClientRect().width ?? 150;
    const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
    const step = cardWidth + gap;
    grid.scrollBy({ left: button.dataset.cbNav === "prev" ? -step : step, behavior: "smooth" });
  }

  openReview() {
    if (this.refs.reviewButton.getAttribute("aria-disabled") === "true") return;
    this.#renderReview();
    this.refs.reviewDialog.showDialog();
  }

  /**
   * @param {{ section?: string }} data - parsed from the `?section=chain`
   *   query-style suffix on the triggering `on:click` attribute (see
   *   component.js's event delegation: a `?`-prefixed data segment is
   *   parsed and passed as the first argument, ahead of the event).
   */
  editSection(data) {
    const section = data?.section;
    this.refs.reviewDialog.closeDialog();

    // On mobile these are single-open (handleSectionToggle closes the
    // others as a side effect of setting `.open = true`); setting both
    // droplet and charm open here would fight each other since each
    // assignment's toggle event closes the section just opened by the
    // previous one. "Extras" starts the customer back at droplet — on
    // desktop #syncAccordionForViewport below opens all three anyway.
    if (section === "chain") {
      this.refs.sectionChain.open = true;
    } else {
      this.refs.sectionDroplet.open = true;
    }
    this.#syncAccordionForViewport();
  }

  /** @param {Event} event */
  async addToCart(event) {
    const button = event.target instanceof HTMLElement ? event.target.closest("button") : null;
    if (!button || button.getAttribute("aria-disabled") === "true") return;

    const selected = [this.selection.chain, ...this.#expandedExtras()].filter(Boolean);
    if (selected.length < 2) return;

    const { addToCartError } = this.refs.reviewDialog.refs;

    button.setAttribute("aria-disabled", "true");
    button.classList.add("btn--loading");
    addToCartError.classList.add("hidden");

    try {
      const resolved = (await Promise.all(selected.map((item) => this.#resolveSelected(item)))).filter(Boolean);
      if (resolved.length !== selected.length) {
        // A product failed to (re-)fetch since it was selected — fail the
        // add rather than submit an incomplete build.
        throw new Error("one or more selected components could not be resolved");
      }

      const bundleInstanceId = generateBundleInstanceId();
      const { min: minExtras } = this.#extrasLimits();
      const experienceProperties = await prepareExperienceAdd("charm-builder");
      // Line-item properties (Shopify's per-line customisation data):
      //   _bundle_instance_id  every line — per-build UUID
      //   _bundle_is_chain     chain only — lets cart-products.liquid find
      //                        the parent without guessing from type/tags
      //   _bundle_min_extras   chain only — the builder's minimum charm
      //                        count, frozen per build so the cart can hide
      //                        "Remove" on charms once the build is at its
      //                        minimum without needing this section loaded
      //   _bundle_slot         extras only — "1", "2", ... in selection
      //                        order. Not shown to the customer: it keeps
      //                        two of the same charm in one build as two
      //                        separate cart lines (identical properties
      //                        would merge into one line with qty 2), so
      //                        each can be removed on its own. The cart
      //                        labels every charm with the *build's*
      //                        number instead ("Custom Charm 1" for the
      //                        whole first build) — see cart-products.liquid
      // All underscore-prefixed so the generic property list hides them.
      //
      // Every extra is sent with `parent_id` = the chain's variant id, the
      // same native nested-line mechanism cart-items.js uses for per-line
      // gift wrap. Shopify then keeps the charms directly under the chain
      // in cart.items, exposes `item.parent_relationship` to Liquid, and
      // removes every charm automatically when the chain line is removed.
      const chainVariantId = resolved[0].variant.id;
      const items = resolved.map(({ variant }, index) => {
        if (index === 0) {
          return {
            id: variant.id,
            quantity: 1,
            properties: {
              ...experienceProperties,
              _bundle_instance_id: bundleInstanceId,
              _bundle_is_chain: "true",
              _bundle_min_extras: String(minExtras),
            },
          };
        }
        return {
          id: variant.id,
          quantity: 1,
          parent_id: chainVariantId,
          properties: {
            ...experienceProperties,
            _bundle_instance_id: bundleInstanceId,
            _bundle_slot: String(index),
          },
        };
      });

      const response = await fetch(
        window.FoxTheme.routes.cart_add_url,
        fetchConfig("json", {
          body: JSON.stringify({
            items,
            sections: this.#gatherGroupedSectionIds().join(","),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();

      if (!response.ok || data.status) {
        addToCartError.textContent = data.description || data.message || "Something went wrong adding this to your cart.";
        addToCartError.classList.remove("hidden");
        return;
      }

      void trackExperience("charm_builder_added_to_cart");
      // Dispatch the same kind of event product-form.js/cart-items.js do on
      // a successful add, so the cart drawer opens/updates itself and any
      // pixel/analytics listeners on `productAjaxAdded` see this add too —
      // matches the theme's native add-to-cart UX rather than a hard page
      // redirect. Uses CartUpdateEvent (absolute count) rather than
      // CartAddEvent (incremental count = items sent) because the Cart
      // Transform Function merges these `items.length` separate lines into
      // one bundle line server-side — the cart's real item-count delta
      // isn't known client-side once a Function may have changed it, so
      // it's read back out of the section HTML the response already
      // includes, the same way cart-items.js's
      // #dispatchCartUpdateFromParsedResponse does.
      this.dispatchEvent(
        new CartUpdateEvent(data, "charm-builder", {
          source: "charm-builder",
          itemCount: this.#parseItemCountFromSections(data.sections),
          sections: data.sections,
        })
      );
      document.dispatchEvent(new CustomEvent(ThemeEvents.productAjaxAdded, { detail: { product: data } }));

      // The build is in the cart now: close the review dialog *and* the
      // builder itself (user request), leaving the cart drawer — opened
      // by the CartUpdateEvent above — as what the customer sees. The
      // selection is reset too, so reopening "Start Your Design" starts a
      // fresh build rather than offering the one just added again.
      this.refs.reviewDialog.closeDialog();
      this.closeDialog();
      this.restart();
    } catch (error) {
      console.error("[charm-builder] add to cart failed", error);
      addToCartError.textContent = "Something went wrong adding this to your cart.";
      addToCartError.classList.remove("hidden");
    } finally {
      button.removeAttribute("aria-disabled");
      button.classList.remove("btn--loading");
    }
  }

  /**
   * Asks every section that cares about cart updates (drawer, cart-count
   * bubble, etc.) for its own section id, via the same broadcast the
   * theme's product form uses (assets/product-form.js), so their HTML can
   * be requested pre-rendered in the `/cart/add.js` response and morphed
   * in without a follow-up fetch.
   * @returns {string[]}
   */
  #gatherGroupedSectionIds() {
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return [...new Set(sections)];
  }

  /**
   * Reads the cart's true total item count out of returned section HTML,
   * mirroring `assets/cart-items.js`'s `#dispatchCartUpdateFromParsedResponse`
   * — the `/cart/add.js` response itself has no cart-wide item-count field
   * for a multi-item add, only the per-line items sent, so the section
   * HTML (rendered server-side against the post-transform cart) is the
   * only accurate source. Returns `undefined` if no section carried the
   * ref (e.g. no sections were requested), matching cart-drawer.js/
   * cart-count.js's own guard for a missing count.
   * @param {Record<string, string> | undefined} sections
   * @returns {number | undefined}
   */
  #parseItemCountFromSections(sections) {
    if (!sections) return undefined;
    for (const html of Object.values(sections)) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      // Same two-tier lookup as product-form.js's #handleSubmit: prefer the
      // cartItemCount ref, fall back to the cart-count bubble's own class —
      // not every registered section is guaranteed to carry the ref.
      const countEl = doc.querySelector('[ref="cartItemCount"]') ?? doc.querySelector(".cart-bubble__text-count");
      const count = countEl?.textContent ? parseInt(countEl.textContent, 10) : NaN;
      if (!Number.isNaN(count)) return count;
    }
    return undefined;
  }

  // The calibration page uses the real storefront renderer. These drafts live
  // only in this browser; authenticated writes belong to the admin app.
  #calibrationCatalog = null;
  #calibrationOriginal = null;
  #calibrationFrame = null;
  #calibrationArrangement = null;
  #calibrationScene = "";

  async calibrationOptions(fullCatalog) {
    if (!this.hasAttribute("data-calibration")) throw new Error("Open the calibration page first.");
    const catalog = fullCatalog ?? await this.#loadCatalog();
    this.#calibrationOriginal = structuredClone(catalog);
    this.#calibrationCatalog = structuredClone(catalog);
    const groups = [...catalog.chains.map(entry => ({...entry, group: "chain"})), ...catalog.components];
    const options = await Promise.all(groups.map(async entry => {
      const product = await this.#fetchProduct(entry.handle);
      if (!product) throw new Error(`The pictures for ${entry.handle} could not be loaded. Reload before exporting so no products are left out.`);
      return this.#allowedVariants(product, entry.variant_filter).map(variant => ({
        handle: entry.handle, productId: String(product.id), variantId: variant.id, group: entry.group,
        hidden: entry.hidden === true,
        title: `${product.title} — ${variant.title}`,
        productTitle: product.title,
        selectedOptions: product.options.map((option, index) => ({name: typeof option === "string" ? option : option.name, value: variant.options[index]})),
        image: this.#resolveImage(product, variant, entry),
        fields: {
          ...placedEditorFields(entry.builder_image?.component?.[variant.id]),
          ...Object.fromEntries(Object.entries(entry.builder_image?.hoop?.[variant.id] ?? {}).map(([key, value]) =>
            [({x: "hoop_x", y: "hoop_y", width: "hoop_width"})[key] ?? key, value])),
        },
      }));
    }));
    return options.flat();
  }

  calibrationFit() { this.#calibrationFrame = null; }

  calibrationRegister(operation) {
    if (!this.hasAttribute("data-calibration")) throw new Error("Open the calibration page first.");
    if ([...this.#calibrationOriginal.chains, ...this.#calibrationOriginal.components].some(e => e.handle === operation.handle)) return;
    const entry = {handle: operation.handle, group: operation.group, builder_image: {variants: {}, component: {}, hoop: {}}};
    this.#calibrationOriginal[operation.group === "chain" ? "chains" : "components"].push(entry);
  }

  /**
   * Editors: download and measure many pictures at once, so drawing them a
   * few at a time afterwards does not wait on the network for each group.
   * @param {string[]} urls
   */
  calibrationPrefetch(urls) {
    return Promise.all([...new Set(urls)].map(measureImageGeometry));
  }

  async calibrationPreview(chain, extras, drafts) {
    if (!this.hasAttribute("data-calibration")) throw new Error("Open the calibration page first.");
    const scene = JSON.stringify([chain, ...extras].map(item => [item.handle, item.selectedOptions?.filter(o => !/^(material|metal|colou?r)$/i.test(o.name))]));
    if (scene !== this.#calibrationScene) { this.#calibrationScene = scene; this.#calibrationFrame = null; this.#calibrationArrangement = null; }
    this.#calibrationCatalog = structuredClone(this.#calibrationOriginal);
    const entries = [...this.#calibrationCatalog.chains, ...this.#calibrationCatalog.components];
    for (const draft of drafts) {
      const entry = entries.find(item => item.handle === draft.handle);
      if (!entry) continue;
      const image = entry.builder_image;
      image.component ??= {}; image.hoop ??= {}; image.variants ??= {};
      const id = draft.variantId;
      for (const [key, value] of Object.entries(draft.fields)) {
        if (["hoop_x", "hoop_y", "hoop_width", "charm_scale", "charm_offset_y", "charm_overlap"].includes(key)) {
          image.hoop[id] ??= {x: 50, y: 88};
          image.hoop[id][({hoop_x: "x", hoop_y: "y", hoop_width: "width"})[key] ?? key] = value;
        } else if (["scale", "offset_x", "offset_y", "pivot_x", "pivot_y", "height_cm", "content_width", "stone_height_mm", "stone_width_mm"].includes(key)) {
          image.component[id] ??= {};
          image.component[id][key] = value;
        } else if (/^(offset_x|offset_y|pivot_x|pivot_y)@\d+$/.test(key)) {
          // The editor's "offset_x@<necklace id>": this charm on one necklace.
          const [name, chainId] = key.split("@");
          image.component[id] ??= {};
          const placed = (image.component[id].necklace_placement ??= {});
          placed[chainId] = {...placed[chainId], [name]: value};
          if (value == null) delete placed[chainId][name];
        }
      }
      if (draft.image) image.variants[id] = draft.image;
    }
    this.selection = {chain, extras: extras.map((item, i) => ({...item, quantity: 1, previewLayers: [i]}))};
    await this.#updatePreview();
  }

  async #loadCatalog() {
    if (this.#calibrationCatalog) return this.#calibrationCatalog;
    if (CharmBuilder.#catalog) return CharmBuilder.#catalog;
    // Rendered inline by sections/charm-builder.liquid, not fetched — it
    // has to be real Liquid output (a `{% render %}`'d metafield lookup
    // per entry) to carry each product's `builder_image`, and
    // assets/*.liquid files are served as raw, unprocessed source by
    // Shopify rather than run through the Liquid engine, so this can't be
    // a separate fetched asset the way it used to be.
    CharmBuilder.#catalog = JSON.parse(this.refs.catalogData.textContent);
    // New products must also reach the customer builder beyond Liquid's first page.
    let next = this.refs.catalogData.dataset.next;
    const catalog = CharmBuilder.#catalog;
    CharmBuilder.#catalog = null;
    const visited = new Set();
    while (next) {
      const url = new URL(next, location.href);
      if (url.origin !== location.origin || visited.has(url.href)) throw new Error("The builder catalogue could not be loaded.");
      visited.add(url.href);
      url.searchParams.set("section_id", "charm-calibration-catalog");
      const response = await fetch(url);
      if (!response.ok) throw new Error("The builder catalogue could not be loaded.");
      const script = new DOMParser().parseFromString(await response.text(), "text/html").querySelector("[data-calibration-catalog]");
      if (!script) throw new Error("The builder catalogue page is missing.");
      const page = JSON.parse(script.textContent);
      // Overflow pages come from the editors' catalogue, which includes hidden products.
      catalog.chains.push(...page.chains.filter(e => !e.hidden)); catalog.components.push(...page.components.filter(e => !e.hidden));
      next = script.dataset.next;
    }
    // Editors work per product; only the customer builder splits sizes.
    if (!this.hasAttribute("data-calibration")) catalog.components = CharmBuilder.#arrangeCards(await this.#splitSizes(catalog.components), catalog.card_order);
    CharmBuilder.#catalog = catalog;
    return CharmBuilder.#catalog;
  }

  /**
   * One card per size (client 2026-10-21: Mini and Large "listed as
   * separate charms"): a droplet/charm whose product has a Size option
   * with several values becomes one entry per value, each pinned to it by
   * `variant_filter`. The lariat "Style" option is not split yet (Moon,
   * Boundaries Babe: undecided with the client).
   * Entries are told apart by `key` (`handle::value`); `handle` stays the
   * product, so fetching, Stack & Save and the cart are unchanged.
   * @param {CatalogEntry[]} components
   */
  async #splitSizes(components) {
    const products = await Promise.all(components.map((entry) => this.#fetchProduct(entry.handle)));
    return components.flatMap((entry, index) => {
      const product = products[index];
      const option = product?.options?.find((o) => /^size$/i.test(o.name) && (o.values?.length ?? 0) > 1);
      if (!option) return [entry];
      return option.values.map((value) => ({
        ...entry,
        key: `${entry.handle}::${value}`,
        size_label: value,
        // Cropped pictures fill their card whatever the size, so the small
        // size's card draws it a little smaller to tell Mini from Large.
        size_small: /^(mini|small|petite)$/i.test(value),
        variant_filter: { option_name: option.name, values: [value] },
      }));
    });
  }

  /**
   * The card order saved by the charm app (collection metafield
   * `charm_builder_card_order`, a list of entry keys) lets the Mini and
   * Large of one charm sit apart. Cards it names are rearranged among
   * themselves; any other card keeps its collection position. Anything
   * unreadable leaves the collection order alone.
   * @param {CatalogEntry[]} components
   * @param {unknown} saved
   */
  static #arrangeCards(components, saved) {
    // Liquid may hand the metafield over as its JSON text.
    if (typeof saved === "string") {
      try { saved = JSON.parse(saved); } catch { return components; }
    }
    if (!Array.isArray(saved) || saved.some((key) => typeof key !== "string")) return components;
    const byKey = new Map(components.map((entry) => [CharmBuilder.entryKey(entry), entry]));
    const ordered = [...new Set(saved)].filter((key) => byKey.has(key));
    const placed = new Set(ordered);
    let cursor = 0;
    return components.map((entry) => placed.has(CharmBuilder.entryKey(entry)) ? byKey.get(ordered[cursor++]) : entry);
  }

  /** A catalog entry's card identity: its product handle unless it was split by size. */
  static entryKey(entry) {
    return entry?.key ?? entry?.handle;
  }

  /**
   * Finds a handle's own catalog entry (for its `variant_filter`, if any)
   * across both the chains and components lists — needed everywhere a
   * selected handle is resolved back to a variant, since `#resolveVariant`
   * requires the entry, not just the product.
   * @param {string} handle
   */
  async #catalogEntry(handle) {
    const catalog = await this.#loadCatalog();
    return (
      catalog.chains.find((entry) => entry.handle === handle) ??
      catalog.components.find((entry) => CharmBuilder.entryKey(entry) === handle) ??
      catalog.components.find((entry) => entry.handle === handle)
    );
  }

  /**
   * Resolves a selected `{handle, metal}` pair (from `selection.chain` or
   * `selection.extras`) all the way to its product + variant, in one
   * place, so every caller (preview, review, add-to-cart) gets the same
   * answer. Returns null if the product fetch failed.
   * A chain selection carries the exact `variantId` the customer tapped
   * (one card per length), which wins over #resolveVariant's metal-only
   * guess as long as that variant still exists on the product and passes
   * the entry's `variant_filter`; otherwise falls back to the guess.
   * @param {{ handle: string, metal: 'gold' | 'silver' | null, variantId?: number }} selected
   */
  async #resolveSelected(selected) {
    const [product, entry] = await Promise.all([this.#fetchProduct(selected.handle), this.#catalogEntry(selected.key ?? selected.handle)]);
    if (!product || !entry) return null;
    const pinned =
      selected.variantId != null
        ? this.#allowedVariants(product, entry.variant_filter).find((v) => v.id === selected.variantId)
        : null;
    const variant = pinned ?? this.#resolveVariant(product, entry, selected.metal);
    return { product, variant, metal: selected.metal, entry };
  }

  /**
   * The one place every card/preview/review image lookup goes through —
   * mirrors #resolveVariant's role for price/id, so the catalog's
   * `builder_image` (custom.charm_builder_image metafield, resolved
   * server-side into each entry by the inline catalog script tag in
   * sections/charm-builder.liquid — see
   * snippets/charm-builder-metafield-image.liquid) can't be
   * silently bypassed by some call site reading `product`/`variant`
   * fields directly.
   *
   * Precedence: the resolved *variant's* own metafield first (some
   * catalog items get distinct per-variant shots, e.g. Gold vs Silver),
   * then the *product*-level metafield, then the variant's own photo,
   * then the product's — most of this catalog's real product photography
   * is on-model lifestyle shots (spec §11), which is why the metafield
   * exists at all, but a missing one at every level shouldn't blank out a
   * card entirely.
   * @param {any} product
   * @param {any} variant
   * @param {CatalogEntry} [entry]
   */
  #resolveImage(product, variant, entry) {
    const images = entry?.builder_image;
    return (
      images?.variants?.[variant.id] ||
      images?.product ||
      variant.featured_image?.src ||
      product.featured_image ||
      product.images?.[0] ||
      ""
    );
  }

  /**
   * A piece's real overall height in cm. Read from the variant's own
   * `charm_builder_component` metaobject entry (`height_cm`, per variant
   * — so the Snake's Mini and Large entries each carry their own; set in
   * admin, comes through `component` via the snippet). A catalog entry
   * may also carry `height_cm` in sections/charm-builder.liquid as a
   * fallback — a plain number, or `{ option_name, values }` keyed by a
   * product option value — though nothing uses that today. Null when
   * neither says, which sends #updatePreview down its fixed-width path.
   * @param {any} component
   * @param {any} entry
   * @param {any} product
   * @param {any} variant
   * @returns {number | null}
   */
  #heightCm(component, entry, product, variant) {
    const fromMetaobject = Number(component?.height_cm);
    if (Number.isFinite(fromMetaobject) && fromMetaobject > 0) return fromMetaobject;
    const spec = entry?.height_cm;
    if (typeof spec === "number") return spec > 0 ? spec : null;
    if (spec && typeof spec === "object") {
      const value = spec.values?.[this.#variantOptionValue(product, variant, spec.option_name)];
      return typeof value === "number" && value > 0 ? value : null;
    }
    return null;
  }

  /**
   * The value a variant holds for a named product option (e.g. "Length"
   * → `17.5"`), or "" when the product has no such option.
   * @param {any} product
   * @param {any} variant
   * @param {string} optionName
   */
  #variantOptionValue(product, variant, optionName) {
    const index = product.options?.findIndex((option) => option.name === optionName);
    if (index == null || index < 0) return "";
    return variant[`option${index + 1}`] ?? "";
  }

  /** @param {string} handle */
  async #fetchProduct(handle) {
    if (this.hasAttribute("data-calibration-app") && this.calibrationProductLoader) return this.calibrationProductLoader(handle);
    if (this.#productCache.has(handle)) return this.#productCache.get(handle);
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        this.#productCache.set(handle, null);
        return null;
      }
      const product = await response.json();
      this.#productCache.set(handle, product);
      return product;
    } catch {
      this.#productCache.set(handle, null);
      return null;
    }
  }

  /**
   * The variants a catalog entry is actually allowed to offer through the
   * builder — after applying its `variant_filter` (if any), from the
   * inline catalog in sections/charm-builder.liquid. Two catalog entries
   * carry a real client-mandated restriction (2026-08-25 Slack, see spec §5):
   * Signature Necklace must only ever offer its Gold-waterproof Material
   * value (never Gold Filled/Sterling, a separate premium line), and
   * Snake Charm must only ever offer its Mini Size value (never Large).
   * Every other product's `variant_filter` is absent, so this is a no-op
   * pass-through for them.
   *
   * @param {any} product
   * @param {{ option_name: string, values: string[] } | undefined} variantFilter
   */
  #allowedVariants(product, variantFilter) {
    if (!variantFilter) return product.variants;
    const optionIndex = product.options?.findIndex((option) => option.name === variantFilter.option_name);
    if (optionIndex == null || optionIndex < 0) return product.variants;
    const optionKey = `option${optionIndex + 1}`;
    const filtered = product.variants.filter((variant) => variantFilter.values.includes(variant[optionKey]));
    // Fall back to the unfiltered list rather than an empty grid if the
    // filter's option/value strings ever drift from what the product
    // actually has (e.g. the client renames a variant in admin) — a wrong
    // variant shown beats no product shown at all.
    return filtered.length > 0 ? filtered : product.variants;
  }

  /**
   * Index of the product option that carries Gold/Silver, or -1. Named
   * "Material" on most of the catalog, but the Athena medallion's is
   * "Color" — with only the name checked it rendered (and would have
   * sold) the Gold variant under the Silver toggle. Any option whose
   * values mention gold/silver is the metal option.
   * @param {any} product
   */
  #metalOptionIndex(product) {
    const index = product.options?.findIndex(
      (option) =>
        option.name === "Material" ||
        option.values?.some((value) => /\b(gold|silver)\b/i.test(String(value)))
    );
    return index == null ? -1 : index;
  }

  /**
   * The single variant a catalog entry currently resolves to, given the
   * active metal toggle for its group (chains have no toggle, so `metal`
   * is null for them) — this is the one path every price/image/id lookup
   * in this class goes through, so a `variant_filter` restriction can
   * never be silently bypassed by some other method reading
   * `product.variants` directly.
   *
   * Matches metal by reading each variant's own `Material` option value
   * (case-insensitive substring on "gold"/"silver") rather than a fixed
   * string pair — this catalog's chains/charms use "Gold (Stainless
   * Steel / Waterproof)" while droplets use "14k Gold Filled" for the
   * same concept (spec §5). Prefers an available match; falls back to
   * any match, then to the first allowed variant, so a card is never
   * left with no resolvable variant at all.
   *
   * @param {any} product
   * @param {{ variant_filter?: { option_name: string, values: string[] } }} entry
   * @param {'gold' | 'silver' | null} metal
   */
  #resolveVariant(product, entry, metal) {
    const allowed = this.#allowedVariants(product, entry.variant_filter);
    if (!metal) return allowed.find((v) => v.available) ?? allowed[0];

    // "Material" on most of the catalog, but the Athena medallion's
    // Gold/Silver option is named "Color" — with only the first name
    // checked it rendered (and would have sold) the Gold variant under
    // the Silver toggle. Any option whose values mention gold/silver is
    // the metal option.
    const materialIndex = this.#metalOptionIndex(product);
    if (materialIndex < 0) {
      // No metal option at all (a single-variant "Default Title"
      // product) — always resolvable regardless of which metal toggle
      // is active, per spec §5.
      return allowed.find((v) => v.available) ?? allowed[0];
    }

    const optionKey = `option${materialIndex + 1}`;
    const matches = allowed.filter((v) => (v[optionKey] ?? "").toLowerCase().includes(metal));
    if (matches.length === 0) return allowed.find((v) => v.available) ?? allowed[0];
    return matches.find((v) => v.available) ?? matches[0];
  }

  async #render() {
    await this.#loadCatalog();
    await this.#renderAllGrids();
  }

  async #renderAllGrids() {
    await Promise.all([this.#renderGrid("chain"), this.#renderGrid("droplet"), this.#renderGrid("charm")]);
  }

  /** @param {'chain' | 'droplet' | 'charm'} group */
  async #renderGrid(group) {
    const catalog = await this.#loadCatalog();
    const entries =
      group === "chain" ? catalog.chains : catalog.components.filter((component) => component.group === group);

    const gridRef = group === "chain" ? "chainGrid" : group === "droplet" ? "dropletGrid" : "charmGrid";
    const grid = this.refs[gridRef];
    if (!grid) return;

    const products = await Promise.all(entries.map((entry) => this.#fetchProduct(entry.handle)));
    // Chains now share the same Gold/Silver toggle as droplet/charm —
    // Signature Necklace's own `variant_filter` (Gold-waterproof only)
    // still applies on top, via #allowedVariants, so its card simply
    // doesn't render while the Silver toggle is active (metalMatches
    // below comes up empty for it).
    const metal = this.#metal[group];

    const cards = products
      .map((product, index) => {
        if (!product) return "";
        const entry = entries[index];

        const allowed = this.#allowedVariants(product, entry.variant_filter);
        // Droplets/charms with a metal toggle: a product with no variant
        // at all matching the active metal (shouldn't happen in this
        // catalog — every droplet/charm has both, per the client's
        // spreadsheet — but defensive against a future catalog edit)
        // simply doesn't render this round, rather than showing the
        // wrong metal's card under the wrong toggle.
        let metalMatches = allowed;
        if (metal) {
          const materialIndex = this.#metalOptionIndex(product);
          if (materialIndex >= 0) {
            const optionKey = `option${materialIndex + 1}`;
            metalMatches = allowed.filter((v) => (v[optionKey] ?? "").toLowerCase().includes(metal));
            if (metalMatches.length === 0) return "";
          }
        }

        if (group === "chain") {
          // One card per chain *product* of the active metal, with a row
          // of Length swatches under it (one per variant; sold-out ones
          // greyed). The card shows the size that's currently chosen for
          // it — the selected variant if this is the selected chain,
          // otherwise the first in-stock length — so price/stock on the
          // card always match a real variant.
          const isSelectedChain = this.selection.chain?.handle === entry.handle;
          const selectedVariant = isSelectedChain
            ? metalMatches.find((v) => v.id === this.selection.chain?.variantId)
            : null;
          const shown = selectedVariant ?? metalMatches.find((v) => v.available) ?? metalMatches[0];
          const sizes = metalMatches
            .map((v) => ({
              id: v.id,
              label: displayLength(this.#variantOptionValue(product, v, "Length") || v.title),
              available: Boolean(v.available),
              active: v.id === shown.id,
            }))
            .sort((a, b) => parseFloat(a.label) - parseFloat(b.label));
          const available = metalMatches.some((v) => v.available);
          return this.#renderCard(product, shown, entry, group, available, isSelectedChain && Boolean(selectedVariant), 0, sizes);
        }

        const variant = this.#resolveVariant(product, entry, metal);
        // Available/sold-out is scoped to the variants this card can
        // actually resolve to right now (the active metal's variants, or
        // all allowed variants for a chain with no toggle) — not the
        // whole product, which could have an available variant in a
        // metal this card isn't currently offering.
        const available = metalMatches.some((v) => v.available);
        const selectedExtra = this.selection.extras.find((extra) => (extra.key ?? extra.handle) === CharmBuilder.entryKey(entry));
        const quantity = selectedExtra ? (selectedExtra.quantity ?? 1) : 0;

        return this.#renderCard(product, variant, entry, group, available, quantity > 0, quantity);
      })
      .join("");

    if (group !== "chain") {
      grid.innerHTML = cards;
      return;
    }

    // Loop the chain row (see #normaliseChainLoop): remember where the
    // customer was within the real set before the markup is replaced
    // (this runs on every selection), lay the set out three times, then
    // put them back at the same spot on the new real set. Clones are
    // kept out of the tab order so keyboard users step through each
    // chain once; clicks on a clone still work, since `choose` reads
    // the card's own data attributes rather than its position.
    const previousSetWidth = this.#chainSetWidth(grid);
    const offsetInSet = previousSetWidth
      ? (((grid.scrollLeft - previousSetWidth) % previousSetWidth) + previousSetWidth) % previousSetWidth
      : grid.scrollLeft;

    grid.dataset.cbLoop = "false";
    grid.innerHTML = cards;
    const realWidth = grid.scrollWidth;
    const shouldLoop = grid.querySelectorAll(".charm-builder__option").length > 1 && realWidth > grid.clientWidth;
    if (!shouldLoop) return;

    const clone = cards.replace(/<(button|a)\b/g, '<$1 data-cb-clone="true" tabindex="-1"');
    grid.dataset.cbLoop = "true";
    grid.innerHTML = clone + cards + clone;
    const setWidth = this.#chainSetWidth(grid);
    grid.scrollLeft = setWidth + offsetInSet;
  }

  /**
   * @param {any} product
   * @param {any} variant - the variant #resolveVariant chose for this
   *   card's current metal toggle / variant_filter state — its own
   *   price/image are used, not the product's first-variant defaults, so
   *   e.g. Curator Charm Necklace shows its 17.5" price ($119) correctly
   *   whichever metal is toggled, and Signature Necklace only ever shows
   *   its Gold-waterproof price, never the Gold Filled premium price.
   * @param {CatalogEntry} entry
   * @param {string} group
   * @param {boolean} available
   * @param {boolean} isSelected
   * @param {number} [quantity] - droplet/charm only: how many of this
   *   piece are selected (0 when not selected)
   * @param {{ id: number, label: string, available: boolean, active: boolean }[]} [sizes]
   *   chain only: one Length swatch per variant of the active metal
   */
  #renderCard(product, variant, entry, group, available, isSelected, quantity = 0, sizes = []) {
    const image = this.#resolveImage(product, variant, entry);
    const title = escapeHtml(displayTitle(product.title));
    // Once the current selection has earned a Stack & Save tier, every
    // card shows what it would cost at that rate (adding a piece can only
    // keep or raise the tier, never lower it), old price struck through.
    const price = priceHtml(variant.price, this.#stackRateFor(product, this.#stackState().rate));
    const classes = ["charm-builder__option"];
    if (isSelected) classes.push("is-selected");
    if (entry.size_small) classes.push("charm-builder__option--small-size");
    // Chains show their lengths as swatches under the card (below), so
    // no separate length line on the card itself.
    // A size-split card names its size ("Mini", "Large", the lariat choice).
    const length = group === "chain" ? "" : entry.size_label ?? "";
    // Chain cutouts carry very different amounts of transparent padding
    // (Lockheart Silver's chain spans ~89% of its PNG, Curator Gold's
    // ~68%), so at one CSS size Curator sat visibly smaller in its card
    // (client: "make sure the chain images fit the gap"). Scale each
    // chain card image by its measured opaque width (`content_width`,
    // the same metaobject field #updatePreview uses for spacing) so
    // every chain spans about the same share of its card. Clamped so a
    // missing/odd value can't blow a chain up past the card's clip.
    const contentWidth = group === "chain" ? entry?.builder_image?.component?.[variant.id]?.content_width : null;
    // Capped at 1.15 (was 1.25): with the image now `object-fit: contain`
    // inside the card, a larger scale pushed the chain past the card's
    // clip on the widest cutouts.
    const fitScale = contentWidth ? Math.min(1.15, Math.max(1, 0.8 / contentWidth)) : 1;
    const imageStyle = fitScale > 1 ? ` style="transform: scale(${fitScale.toFixed(3)})"` : "";

    // Keep sold-out pieces visible without allowing selection or
    // navigation away from the builder.
    const interactionAttrs = available
      ? `type="button" on:click="charm-builder/choose"`
      : `type="button" disabled`;

    // Every card is wrapped in a slot so it can carry controls *beside*
    // the card button — a <button> can't contain other buttons: a
    // "−"/"+" quantity picker for a selected droplet/charm, a row of
    // Length swatches for a chain. The looping chain row still measures
    // and clones by the `.charm-builder__option` buttons inside the
    // slots (see #chainSetWidth), which is unaffected by the wrapper.
    const swatches =
      group === "chain" && sizes.length
        ? `
        <span class="charm-builder__sizes" role="group" aria-label="${title} length">
          ${sizes
            .map(
              (size) => `<button type="button" class="charm-builder__size${size.active ? " is-active" : ""}"
                on:click="charm-builder/chooseChainSize?variant=${size.id}"
                aria-pressed="${size.active}"${size.available ? "" : ' disabled aria-label="' + escapeHtml(size.label) + ' — sold out"'}
              >${escapeHtml(size.label)}</button>`
            )
            .join("")}
        </span>`
        : "";
    const picker =
      group !== "chain" && isSelected
        ? `
        <span class="charm-builder__qty" role="group" aria-label="Quantity of ${title}">
          <button type="button" class="charm-builder__qty-btn" on:click="charm-builder/adjustQuantity?delta=-1" aria-label="Remove one ${title}">−</button>
          <span class="charm-builder__qty-value" aria-live="polite">${quantity}</span>
          <button type="button" class="charm-builder__qty-btn" on:click="charm-builder/adjustQuantity?delta=1" aria-label="Add one more ${title}"${
            this.#extrasTotal() >= this.#extrasLimits().max ? " disabled" : ""
          }>+</button>
        </span>`
        : "";
    const key = escapeHtml(CharmBuilder.entryKey(entry));
    const openSlot = `<div class="charm-builder__option-slot${isSelected ? " is-selected" : ""}" data-handle="${escapeHtml(entry.handle)}" data-key="${key}">`;
    const closeSlot = `${swatches}${picker}</div>`;

    return `${openSlot}
      <button
        class="${classes.join(" ")}"
        data-cb-group="${group}"
        data-handle="${escapeHtml(entry.handle)}"
        data-key="${key}"
        ${group === "chain" ? `data-variant-id="${variant.id}"` : ""}
        aria-disabled="${!available}"
        ${interactionAttrs}
      >
        <span class="charm-builder__option-card">
          <img class="charm-builder__option-image" src="${imageAtWidth(image, 400)}" srcset="${imageAtWidth(image, 400)} 1x, ${imageAtWidth(image, 800)} 2x" width="187" height="187" alt="${title}" loading="lazy"${imageStyle}>
        </span>
        <span class="charm-builder__option-info">
          <span class="charm-builder__option-label">${title}</span>
          ${length ? `<span class="charm-builder__option-meta">${escapeHtml(length)}</span>` : ""}
          <span class="charm-builder__option-price">${price}</span>
        </span>
      </button>${closeSlot}
    `;
  }

  async #updatePreview() {
    const { previewMain } = this.refs;

    if (!this.selection.chain) {
      previewMain.innerHTML = `<p class="charm-builder__preview-main-placeholder">Choose a chain to begin your design</p>`;
      return;
    }

    const resolvedChain = await this.#resolveSelected(this.selection.chain);
    const resolvedExtras = (await Promise.all(this.#expandedExtras().map(async (extra) => {
      const resolved = await this.#resolveSelected(extra);
      return resolved ? { ...resolved, previewLayer: extra.previewLayer } : null;
    }))).filter(Boolean);

    if (!resolvedChain) return;

    const { product: chainProduct, variant: chainVariant, entry: chainEntry } = resolvedChain;
    const chainImage = this.#resolveImage(chainProduct, chainVariant, chainEntry);

    // Every builder_image is a square "generated" cutout (chain and charm
    // alike) — see memory project_charm_builder_metaobject_catalog.md —
    // so a square canvas needs no per-image aspect-ratio math: the chain
    // fills it with zero letterboxing, and percentage positions map 1:1
    // onto the chain's own source image regardless of how the outer
    // (non-square, "full height") .charm-builder__preview-main box is
    // shaped at a given viewport.
    //
    // Where the charms cluster vertically is NOT one constant, though —
    // Curator has a round ring, Lockheart a heart-shaped clasp sitting
    // higher, and Signature has no ring graphic at all (its clasp is at
    // the *top* of the photo, so charms just gather at the chain's
    // bottom vertex). Each chain's own `hoop_y` (a metaobject field,
    // percent down its own photo, measured off the source image's opaque
    // pixels — see memory project_charm_builder_metaobject_catalog.md)
    // comes through on the *selected chain's resolved entry* via
    // #resolveImage's sibling data, `entry.builder_image.hoop`. Falls
    // back to .charm-builder__preview-charms' own CSS `top` (tuned for
    // Curator) if a chain's entry has no hoop_y set yet. hoop_x is the
    // cluster's horizontal centre the same way (default 50) — the
    // charms' own `left` percentages are relative to it, so
    // .preview-charms can keep spanning the full canvas width. All
    // charms share the one chosen `top` (no per-charm vertical offset)
    // so they visually hang from one shared point.
    //
    // Real charms sharing one small ring get pushed against each other
    // and lean — bails staying together at the ring, bodies swinging
    // toward the middle below it — not fanning outward like an open
    // hand. Each charm's centre position and rotation are both driven by
    // its signed distance from the middle of the row (`offset`):
    //   - Position: `left` is a plain percentage of the canvas width, not
    //     a flex-row + negative-margin layout (the original approach) —
    //     that version had rotation and spacing fighting each other,
    //     because rotating a charm inflates its *axis-aligned bounding
    //     box* (a rotated rectangle's bbox is wider than the rectangle),
    //     and margin-based spacing reacts to that inflated box. Turning
    //     up rotation alone silently widened the whole row even with
    //     spacing unchanged, and past a certain negative margin the
    //     layout order visually reversed (measured live: the first charm
    //     in DOM order rendered rightmost). Positioning by an explicit
    //     `left: %` + `translateX(-50%)` makes spacing depend only on
    //     CHARM_SPACING_STEP_PERCENT, never on the charm's own rotated
    //     size — CHARM_WIDTH_PERCENT (below) can be tuned independently
    //     to control how much that spacing overlaps.
    //   - Rotation: a LEFT charm (offset < 0) needs its lower end
    //     swinging right, toward the middle — inward — and a RIGHT charm
    //     (offset > 0) needs its lower end swinging left, also inward.
    //     Client screenshot (2026-09-05) showed the previous sign
    //     (`-offset * step`) rendering the opposite of that — charms
    //     leaning away from centre — so this is the corrected sign,
    //     confirmed against the rendered result rather than reasoned
    //     from CSS rotate()'s clockwise-positive convention alone (that
    //     reasoning is what produced the wrong sign the first time).
    // Pivoting from the top (the bail, see
    // .charm-builder__preview-charm-img's transform-origin) keeps this
    // reading as "hanging from that point", not the whole charm sliding
    // sideways.
    //
    // `component.scale`/`offset_x`/`offset_y` are optional per-entry
    // metaobject fields (charm_builder_component) for the client to
    // hand-tune one specific charm that still looks off after these
    // global constants — see memory
    // project_charm_builder_metaobject_catalog.md. Blank on every entry
    // by default, so unset means "use the shared position exactly", not
    // some silently-different fallback value.
    const CHARM_FAN_DEG = 22;
    const CHARM_MAX_BAIL_SPREAD_PERCENT = 11;
    const CHARM_BOX_WIDTH_PERCENT = 17;
    const CHARM_OVERLAP_BASE = 0.42;
    const CHARM_OVERLAP_PER_EXTRA = 0.07;
    const CHARM_OVERLAP_MAX = 0.62;
    const DEFAULT_CONTENT_WIDTH = 0.5;
    const charmCenter = (resolvedExtras.length - 1) / 2;

    // Charms have to bunch up more as more are added, or the cluster just
    // keeps growing sideways off the hoop — the client's words: "4 charms
    // works but the 5th one pushes them out too much". A real handful of
    // charms on one ring crowds together rather than spreading into a
    // wider arc, so overlap scales with the count from the 3-charm
    // minimum upward.
    //
    // The per-chain tuning below (`chainHoop`) is what makes the same
    // cluster maths fit three very different clasps. Curator's ring,
    // Lockheart's heart and Signature's bare chain vertex each need
    // their own charm size, ring width, vertical nudge and overlap —
    // one global constant was only ever right for the chain it was
    // tuned on (client, 2026-09-06: "they need to overlap a little bit
    // more and be moved downwards on certain charms ... ideally they
    // would change per necklace"). Every field is optional on the
    // metaobject; blank falls back to the constants above.
    const chainHoop = chainEntry?.builder_image?.hoop?.[chainVariant.id];
    const chainCharmScale = chainHoop?.charm_scale ?? 1;

    // Where the bails start, percent down the canvas. `hoop_y` is the
    // chain's ring as measured off its photo; `charm_offset_y` nudges
    // the cluster relative to it, because where the bails should *start*
    // differs by clasp shape — Lockheart's ring hole is at the bottom
    // point of its heart, well below the heart's centre. 88 is the CSS
    // fallback tuned for Curator.
    //
    // Clamped to the chain photo's own lowest opaque pixel (measured
    // live, same as the charms): hoop_y is typed into admin once, off
    // one photo, and when the chain is re-shot it silently goes stale —
    // Signature's stored 94% was ~4% BELOW the bottom of its new photo,
    // so its charms floated in space under the chain. A bail can never
    // hang below the thing it's threaded on; 1.5% up from the edge
    // leaves the bail wrapped round the wire rather than kissing it.
    const chainGeometry = await measureImageGeometry(chainImage);
    // Gold and silver are one physical design. Use the gold picture of this
    // exact length as their common safety boundary; different transparent
    // crops must not move the attachment when only the metal changes.
    // This applies to the customer renderer too, so saved editor positions
    // use the same boundary after import. Single-metal designs use themselves.
    const metalIndex = this.#metalOptionIndex(chainProduct);
    const pairedVariant = metalIndex < 0 ? chainVariant :
      this.#allowedVariants(chainProduct, chainEntry.variant_filter).find(candidate =>
        /\bgold\b/i.test(candidate.options?.[metalIndex] ?? "") &&
        candidate.options?.every((value, index) => index === metalIndex || value === chainVariant.options?.[index])
      ) ?? chainVariant;
    const pairedHoop = chainEntry?.builder_image?.hoop?.[pairedVariant.id];
    const sharedPlacement = ["x", "y", "charm_offset_y"].every(key =>
      (pairedHoop?.[key] ?? null) === (chainHoop?.[key] ?? null));
    // Keep existing independently calibrated photographs unchanged until the
    // editor explicitly gives the two metals shared placement settings.
    const referenceVariant = sharedPlacement ? pairedVariant : chainVariant;
    const referenceImage = this.#resolveImage(chainProduct, referenceVariant, chainEntry);
    const referenceGeometry = referenceImage === chainImage ? chainGeometry : await measureImageGeometry(referenceImage);
    const chainBottom = referenceGeometry ? (referenceGeometry.top + referenceGeometry.height) * 100 - 1.5 : Infinity;
    const charmsTop = Math.min((chainHoop?.y ?? 88) + (chainHoop?.charm_offset_y ?? 0), chainBottom);
    // `hoop_x` is wired since 2026-09-16: Curator and Signature both
    // centre within a point of 50%, but Lockheart's heart clasp sits at
    // ~46.5% — with the cluster pinned to 50% its charms hung beside the
    // heart rather than from it. Per-charm `left` below is relative to
    // this, so the whole cluster follows the clasp.
    const hoopX = chainHoop?.x ?? 50;
    const charmsTopStyle = ` style="top: ${charmsTop.toFixed(3)}%;"`;
    const chainOverlapBase = chainHoop?.charm_overlap ?? CHARM_OVERLAP_BASE;
    const overlapFraction = Math.min(
      CHARM_OVERLAP_MAX,
      chainOverlapBase + Math.max(0, resolvedExtras.length - 3) * CHARM_OVERLAP_PER_EXTRA
    );

    // Spacing can't be one constant: these PNGs are square boxes with very
    // different amounts of transparent padding around the actual charm.
    // Measured off the source files, a wire-wrapped droplet fills only
    // ~29-39% of its box's width while the Athena medallion fills ~76% —
    // so at an identical CSS width, a droplet's *visible* shape is barely
    // a third as wide as the medallion's. A fixed centre-to-centre step
    // therefore left droplets with visible gaps (they read as unattached)
    // at the same value that made medallions overlap heavily. Each entry
    // now carries `content_width` (opaque fraction of its box, 0-1,
    // auto-measured and stored on the metaobject — see memory
    // project_charm_builder_metaobject_catalog.md), and spacing is
    // derived from the *visible* widths of each neighbouring pair, so any
    // mix of shapes overlaps by the same proportion.
    // Read before sizing the charms below: how big a charm should look
    // depends on the chain it's hanging from. The Signature chain is a
    // fine, delicate cable where a charm sized for the chunky Curator
    // links reads as far too big; Lockheart sits between the two. Each
    // chain entry carries its own `charm_scale` (metaobject field,
    // 1 = default) so this is the client's to adjust per chain in
    // admin — client: "the chains need a way to define what zoom the
    // droplets/charms get per necklace".
    //
    // Since 2026-09-16 the box width is derived from the piece's REAL
    // size where the catalog knows it (`height_cm` on each variant's
    // charm_builder_component metaobject entry — see #heightCm — filled
    // from the client's size sheet, 2026-09-16) rather than
    // one shared CHARM_BOX_WIDTH_PERCENT: a 2cm droplet next to a 3.2cm
    // medallion next to a 6.7cm spiral should look exactly that
    // different, and the old fixed width rendered the Athena medallion
    // and the horseshoe the same size while the droplet beside them
    // shrank to nothing. The chain photos set the scale: all three
    // measure ~130px per cm on their 2380px square (Curator's 1.5cm ring
    // is ~200px tall, the 17.5"/16" loops' perimeters agree), so the
    // canvas is ~18.3cm across. Each image's own opaque outline
    // (`geometry`, measured live — see measureImageGeometry) converts
    // that physical height into the box size that shows it at that
    // height. Per-chain `charm_scale` and per-charm `scale` still
    // multiply on top for hand-tuning in admin. Entries with no
    // `height_cm` keep the old fixed-width sizing.
    const CANVAS_WIDTH_CM = 18.3;
    const geometries = await Promise.all(
      resolvedExtras.map(({ product, variant, entry }) => measureImageGeometry(this.#resolveImage(product, variant, entry)))
    );
    const charmMeta = resolvedExtras.map(({ product, variant, entry }, index) => {
      // A charm can have its own position and hook point on this necklace
      // (`necklace_placement`, keyed by necklace product id); anything it
      // doesn't set comes from the charm's usual settings.
      const stored = entry?.builder_image?.component?.[variant.id];
      const placed = stored?.necklace_placement?.[chainProduct.id];
      const component = placed ? {...stored, ...Object.fromEntries(
        Object.entries(placed).filter(([, value]) => Number.isFinite(value)))} : stored;
      const geometry = geometries[index];
      const scale = component?.scale ?? 1;
      const heightCm = this.#heightCm(component, entry, product, variant);
      const stoneHeightMm = Number(component?.stone_height_mm);
      const stoneWidthMm = Number(component?.stone_width_mm);
      const hasStoneDimensions = entry.group === "droplet" && geometry?.stoneHeight > 0
        && geometry?.stoneWidth > 0 && stoneHeightMm > 0 && stoneWidthMm > 0;
      let boxWidth;
      let boxHeight;
      if (hasStoneDimensions) {
        const percentPerMm = 100 / (CANVAS_WIDTH_CM * 10) * chainCharmScale * scale;
        boxHeight = stoneHeightMm * percentPerMm / geometry.stoneHeight;
        boxWidth = stoneWidthMm * percentPerMm / geometry.stoneWidth;
      } else if (heightCm && geometry) {
        const visibleHeight = (heightCm / CANVAS_WIDTH_CM) * 100 * chainCharmScale * scale;
        boxHeight = visibleHeight / geometry.height;
        boxWidth = boxHeight * geometry.aspect;
      } else {
        boxWidth = CHARM_BOX_WIDTH_PERCENT * chainCharmScale * scale;
      }
      const contentWidth = geometry?.width ?? component?.content_width ?? DEFAULT_CONTENT_WIDTH;
      boxHeight ??= boxWidth / (geometry?.aspect ?? 1);
      return {
        component,
        scale,
        geometry,
        boxWidth,
        boxHeight,
        hasStoneDimensions,
        visibleWidth: boxWidth * contentWidth,
        // How far the shape hangs below its bail — what a rotation about
        // the bail actually swings sideways.
        bodyLength: boxHeight * (geometry?.height ?? 1) * (1 - (component?.pivot_y ?? 0) / 100),
        // Where the bail is inside the box, as fractions of the box: the
        // opaque top edge and the shape's horizontal centre.
        bailTop: (geometry?.top ?? 0) + (geometry?.height ?? 1) * (component?.pivot_y ?? 0) / 100,
        bailCx: (geometry?.left ?? 0) + (geometry?.width ?? 1) * (component?.pivot_x ?? 50) / 100,
      };
    });

    // Lay the visible shapes out left to right, each overlapping its
    // neighbour by `overlapFraction` (above) of their average visible width,
    // then recentre the finished run on the hoop.
    const centres = [];
    let cursor = 0;
    charmMeta.forEach((meta, index) => {
      if (index > 0) {
        const previous = charmMeta[index - 1];
        // Overlap is a fraction of the NARROWER of the pair, not of their
        // average: with true-size pieces a 2cm droplet (visible ~4% of
        // the canvas) next to a 3.2cm medallion (~15%) was stepped by
        // less than the medallion's own half-width and vanished behind
        // it. For two equal pieces this is the same number as before.
        cursor +=
          (previous.visibleWidth + meta.visibleWidth) / 2 -
          overlapFraction * Math.min(previous.visibleWidth, meta.visibleWidth);
      }
      centres.push(cursor);
    });
    // Centre on the run's visible *extent*, not on its first/last centre
    // points — a wide charm (the Athena medallion is more than twice a
    // droplet's visible width) on one end otherwise hangs further out
    // that side and the whole cluster sits visibly off-centre under the
    // hoop.
    //
    // Guarded for the empty case: a chain is routinely selected with no
    // charms at all (before any are picked, and again whenever switching
    // to a chain in the other metal clears them), and indexing [0] of an
    // empty array here threw — which aborted this whole method before it
    // wrote the canvas, so the preview silently kept whatever it was
    // showing. That looked exactly like "changing the chain does
    // nothing", and it hid behind the fact that selecting a chain and
    // its charms together ends with a successful render.
    const first = charmMeta[0];
    const last = charmMeta[charmMeta.length - 1];
    const runMidpoint = first
      ? (centres[0] - first.visibleWidth / 2 + (centres[centres.length - 1] + last.visibleWidth / 2)) / 2
      : 0;

    // Every charm's bail has to sit ON the hoop — that's what makes the
    // cluster read as threaded onto the chain rather than floating below
    // it, and it's the thing several earlier attempts got wrong.
    //
    // The tilt direction that achieves it is the counter-intuitive part.
    // Tilting each charm's *body* inward (toward the centre) forces the
    // bails apart to compensate, and a row of bails spread wider than
    // the hoop reads as unattached no matter how much the bodies
    // overlap — that was the repeated complaint. Tilting the other way
    // (bodies fanning outward) pulls the bails *together* onto the hoop,
    // which is both how real charms hang from a single ring and what
    // finally looked right. So the charms pivot inward at the top even
    // though each body leans outward at the bottom.
    //
    // The angle is the outermost charm's tilt, scaled linearly for the
    // ones between (not a per-step angle multiplied by index, which sent
    // the outer charms to extreme angles as soon as there were four or
    // five). If the bails would still spread wider than the hoop, the
    // whole arrangement is squeezed horizontally to fit — that pulls the
    // bodies closer too, which is the intended trade: more charms means
    // more overlap, never bails drifting off the hoop.
    const maxOffset = Math.max(charmCenter, 1);
    // A long piece hangs straighter than a short one: gravity keeps a
    // lariat's chain near vertical, and a 2cm droplet can swing where an
    // 8cm lariat can't. So the fan angle is capped per piece such that the
    // body's sideways swing (half its length x sin(angle)) never exceeds
    // CHARM_MAX_SWING_PERCENT of the canvas — droplets keep the full fan,
    // a 3.7cm horseshoe gets ~14deg, the 8.5cm Boundaries Babe lariat ~5deg.
    // Without this the lariat's heart swung a quarter of the canvas wide
    // of the clasp (client screenshot, 2026-09-17).
    const CHARM_MAX_SWING_PERCENT = 3;
    let rotations = resolvedExtras.map((_, index) => {
      const raw = -((index - charmCenter) / maxOffset) * CHARM_FAN_DEG;
      const halfBody = Math.max(charmMeta[index].bodyLength / 2, 1e-6);
      const cap = (Math.asin(Math.min(1, CHARM_MAX_SWING_PERCENT / halfBody)) * 180) / Math.PI;
      return Math.max(-cap, Math.min(cap, raw));
    });
    const rawPivotOffsets = charmMeta.map((meta, index) => {
      // Rotating about the bail swings the body sideways by (distance
      // below the bail) x sin(angle); offsetting the pivot by that same
      // amount is what puts the *body* where `centres` wants it.
      const swing = (meta.bodyLength / 2) * Math.sin((rotations[index] * Math.PI) / 180);
      return centres[index] - runMidpoint + swing;
    });
    // Centre the BAILS on the hoop, not the bodies. `centres`/`runMidpoint`
    // balance the run by its visible extent, which is right for spacing
    // but wrong for where the run sits: a long lariat (Boundaries Babe
    // is 8.5cm, tilted 22deg) swings its body far to one side, and
    // balancing that body pulled every bail the other way — client
    // (2026-09-17, Lockheart + 3 droplets + horseshoe + lariat): the
    // whole cluster hung 4% left of the heart clasp. Charms thread onto
    // one ring, so the bails are what must straddle it; the bodies land
    // wherever their tilt takes them.
    // Bail-to-bail spacing is capped at roughly one bail's width. The
    // run above spaces charms by their *visible* widths so bodies overlap
    // by a set fraction — right between two droplets, but a wide body
    // (the 4cm spiral is ~5x a droplet) then wants its centre far from
    // its neighbour, which drags its BAIL far along the ring too: with
    // four droplets and the spiral, the droplets' bails bunched into
    // 1.8% of the canvas and the spiral's sat 3.2% away on the ring's
    // far edge (client, 2026-09-17: "they should be closer to the
    // charms"). On a real ring every bail hangs next to the last; the
    // bodies overlap, with the most recently added piece in front.
    // So each step between neighbouring bails is
    // limited to CHARM_MAX_BAIL_STEP_PERCENT; the squeeze below still
    // compresses the whole run if it exceeds the ring's opening.
    const CHARM_MAX_BAIL_STEP_PERCENT = 1.3;
    const steppedPivotOffsets = rawPivotOffsets.reduce((acc, offset, index) => {
      if (index === 0) return [offset];
      const step = Math.max(-CHARM_MAX_BAIL_STEP_PERCENT, Math.min(CHARM_MAX_BAIL_STEP_PERCENT, offset - rawPivotOffsets[index - 1]));
      return [...acc, acc[index - 1] + step];
    }, []);
    const pivotMid = (Math.max(...steppedPivotOffsets) + Math.min(...steppedPivotOffsets)) / 2;
    let pivotOffsets = steppedPivotOffsets.map((offset) => offset - pivotMid);
    const pivotSpread = Math.max(...pivotOffsets) - Math.min(...pivotOffsets);
    // The cap on how far apart the bails may sit is the chain's own ring
    // opening (`hoop_width`, percent of its photo) when the entry has
    // one. The old fixed 11% was measured for nothing in particular and
    // is roughly twice Curator's actual opening, which is exactly why the
    // outermost droplets' bails landed on the ring's outer edges rather
    // than inside it.
    const maxBailSpread = chainHoop?.width ?? CHARM_MAX_BAIL_SPREAD_PERCENT;
    let squeeze = pivotSpread > maxBailSpread ? maxBailSpread / pivotSpread : 1;

    if (this.hasAttribute("data-calibration")) {
      const keys = ["scale", "offset_x", "offset_y", "pivot_x", "pivot_y", "height_cm", "stone_height_mm", "stone_width_mm"];
      const key = JSON.stringify([this.#calibrationScene, chainHoop,
        charmMeta.map(meta => keys.map(name => meta.component?.[name] ?? null))]);
      if (this.#calibrationArrangement?.key === key) {
        ({ rotations, pivotOffsets, squeeze } = this.#calibrationArrangement);
      } else this.#calibrationArrangement = {key, rotations, pivotOffsets, squeeze};
    }

    const charmsHtml = resolvedExtras
      .map(({ product, variant, entry, previewLayer }, index) => {
        const image = this.#resolveImage(product, variant, entry);
        const { component, boxWidth, boxHeight, bailTop, bailCx, hasStoneDimensions } = charmMeta[index];
        const rotate = rotations[index];
        // `left` places the BAIL (the shape's opaque top-centre) on the
        // hoop, not the box's centre: the box is centred by
        // translateX(-50%), so shift by however far the visible shape's
        // centre sits from the box's own.
        const left = hoopX + pivotOffsets[index] * squeeze + (0.5 - bailCx) * boxWidth + (component?.offset_x ?? 0);
        // Likewise the box is pulled up by its transparent top padding
        // so the bail itself starts at the hoop line, whatever the crop.
        // margin-top, not top: .preview-charms is a zero-height
        // container, so a percentage `top` resolves to 0 — margin
        // percentages resolve against the containing block's WIDTH,
        // which on this square canvas is the same thing as its height.
        const top = -bailTop * boxHeight + (component?.offset_y ?? 0);
        // No loading="lazy" here (or on the chain img below) — a teammate
        // session found it doesn't reliably fire once this markup is
        // injected via innerHTML into a dialog that's already open/in
        // the top layer: images sat at naturalWidth 0 for 5+ seconds
        // with no network slowness involved, confirmed by forcing
        // loading='eager' on a stuck instance and watching it load
        // instantly. These are already only rendered on selection
        // change, so eager-loading them isn't the waste it would be for
        // an always-present image.
        // `scale` is applied as real layout width, not a transform: the
        // spacing maths above sizes gaps from each charm's box width, so a
        // transform-only scale would move the visible shape without the
        // spacing knowing about it.
        // Rotation pivots at the bail (not the box's top-centre) so a
        // tilted charm still hangs from the point it's threaded on.
        // The newest addition sits on top, regardless of its size or
        // where this copy sits in the grouped selection array.
        const zIndex = previewLayer;
        const style =
          `left: ${left.toFixed(3)}%; margin-top: ${top.toFixed(3)}%; width: ${boxWidth.toFixed(3)}%; z-index: ${zIndex}; ` +
          (hasStoneDimensions ? `aspect-ratio: ${boxWidth} / ${boxHeight}; object-fit: fill; ` : "") +
          `transform-origin: ${(bailCx * 100).toFixed(2)}% ${(bailTop * 100).toFixed(2)}%; ` +
          `transform: translateX(-50%) rotate(${rotate.toFixed(2)}deg);`;
        return `<img class="charm-builder__preview-charm-img" data-handle="${escapeHtml(entry.handle)}" data-variant-id="${variant.id}" ${this.hasAttribute("data-calibration") ? `data-mm-width="${charmMeta[index].visibleWidth * CANVAS_WIDTH_CM * 10 / 100 / chainCharmScale}" data-mm-height="${charmMeta[index].boxHeight * (charmMeta[index].geometry?.height ?? 1) * CANVAS_WIDTH_CM * 10 / 100 / chainCharmScale}" data-geometry='${JSON.stringify(charmMeta[index].geometry ?? {left: 0, top: 0, width: 1, height: 1})}'` : ""} style="${style}" src="${image}" alt="${escapeHtml(displayTitle(product.title))}">`;
      })
      .join("");

    // Framing: which slice of the square chain photo the panel shows.
    //
    // The canvas used to be "the whole photo, shrunk to fit, plus a
    // reserve below for the deepest charm". With charms now at their
    // real size (a droplet is ~2cm against a ~40cm chain) that framing
    // rendered the charms as specks under a big empty loop of chain —
    // client, 2026-09-17: "is there any way we can have the necklace
    // raised higher? Even if it's cutting off maybe half of the
    // necklace?". So the photo is now cropped to a window around the
    // hoop: it starts CHAIN_ABOVE_HOOP of the photo above the bails
    // (enough chain to read as a necklace, not enough to dwarf the
    // charms) and ends just under the deepest charm or the chain's own
    // bottom, whichever is lower. The square photo is then scaled so
    // that window fills the panel's height — the top of the chain is
    // simply outside the viewport. The scale is also capped so the
    // chain's own opaque width (and the charm cluster's) still fits
    // the panel's width with a small margin; a chain cropped at the
    // *sides* would read as a mistake where one cropped at the top
    // reads as a close-up.
    //
    // All three numbers are fractions of the square photo's side and
    // are handed to the CSS (assets/charm-builder.css
    // .charm-builder__preview-canvas), which does the fitting with
    // container-query units; the JS only knows the geometry.
    const CHAIN_ABOVE_HOOP = 0.42;
    const FRAME_PAD = 0.03;
    const deepest = charmMeta.reduce((max, meta) => Math.max(max, meta.bodyLength + (meta.component?.offset_y ?? 0)), 0);
    const chainBottomEdge = chainGeometry ? chainGeometry.top + chainGeometry.height : 1;
    const frameTop = Math.max(0, charmsTop / 100 - CHAIN_ABOVE_HOOP);
    const frameBottom = Math.max(chainBottomEdge, (charmsTop + deepest) / 100) + FRAME_PAD;
    const frameHeight = frameBottom - frameTop;
    // Widest thing that must stay inside the panel, as a fraction of the
    // square: the chain's opaque width or the cluster's extent (each
    // charm's box centre ± half its width, relative to the canvas
    // centre), plus a margin either side.
    const clusterHalfWidth = charmMeta.reduce((max, meta, index) => {
      const left = hoopX + pivotOffsets[index] * squeeze + (0.5 - meta.bailCx) * meta.boxWidth + (meta.component?.offset_x ?? 0);
      return Math.max(max, Math.abs(left - 50) + meta.boxWidth / 2);
    }, 0);
    const frameWidth = Math.max(chainGeometry?.width ?? 1, (clusterHalfWidth * 2) / 100) + FRAME_PAD * 2;
    let frameStyle =
      `--charm-builder-frame-top: ${frameTop.toFixed(3)}; ` +
      `--charm-builder-frame-height: ${frameHeight.toFixed(3)}; ` +
      `--charm-builder-frame-width: ${Math.min(1, frameWidth).toFixed(3)};`;

    // The editor must fit even an accidentally huge or off-canvas charm,
    // including when reopening a saved draft or moving to another design.
    // Include every rotated image corner, with no one-canvas width cap. This
    // camera adjustment changes no product settings or customer framing.
    if (this.hasAttribute("data-calibration")) {
      let top = frameTop, bottom = frameBottom, width = frameWidth;
      charmMeta.forEach((meta, index) => {
        const angle = rotations[index] * Math.PI / 180;
        const bailX = hoopX + pivotOffsets[index] * squeeze + (meta.component?.offset_x ?? 0);
        const bailY = charmsTop + (meta.component?.offset_y ?? 0);
        for (const x of [-meta.bailCx * meta.boxWidth, (1 - meta.bailCx) * meta.boxWidth]) {
          for (const y of [-meta.bailTop * meta.boxHeight, (1 - meta.bailTop) * meta.boxHeight]) {
            const px = (bailX + x * Math.cos(angle) - y * Math.sin(angle)) / 100;
            const py = (bailY + x * Math.sin(angle) + y * Math.cos(angle)) / 100;
            top = Math.min(top, py - FRAME_PAD);
            bottom = Math.max(bottom, py + FRAME_PAD);
            width = Math.max(width, Math.abs(px - 0.5) * 2 + FRAME_PAD * 2);
          }
        }
      });
      frameStyle = `--charm-builder-frame-top: ${top.toFixed(3)}; --charm-builder-frame-height: ${(bottom - top).toFixed(3)}; --charm-builder-frame-width: ${width.toFixed(3)};`;
    }

    // Keep placement and metal comparisons steady. The editor invalidates
    // this frame when sizes change, so growing charms bring the camera back.
    if (this.hasAttribute("data-calibration")) {
      this.#calibrationFrame ??= frameStyle;
      frameStyle = this.#calibrationFrame;
    }

    previewMain.innerHTML = `
      <div class="charm-builder__preview-viewport">
        <div class="charm-builder__preview-canvas" style="${frameStyle}">
          <img class="charm-builder__preview-chain-img" src="${imageAtWidth(chainImage, 3000)}" alt="${escapeHtml(chainProduct.title)}">
          <div class="charm-builder__preview-charms"${charmsTopStyle}>${charmsHtml}</div>
        </div>
      </div>
    `;
  }

  /**
   * "(x of 5 added)" next to the droplet/charm headings. Droplets and
   * charms share one pool and one cap, so both headings show the same
   * count. Called from #updateReviewButton, which every selection change
   * (choose/restart) already goes through.
   */
  #updateExtrasCount() {
    const { max } = this.#extrasLimits();
    const count = this.#extrasTotal();
    for (const el of this.refs.extrasCount ?? []) {
      el.textContent = `(${count} of ${max} added)`;
    }
  }

  async #updateReviewButton() {
    this.#updateExtrasCount();
    this.#updateStackProgress();
    const ready = Boolean(this.selection.chain) && this.#extrasTotal() >= this.#extrasLimits().min;
    this.refs.reviewButton.setAttribute("aria-disabled", String(!ready));

    // Running total appended to the button label ("Review Your Design |
    // $289.00") so the price is visible while building, not only on the
    // review screen. Hidden until something is chosen. Resolved the same
    // way #renderReview totals it, so the two never disagree.
    const { reviewButtonPrice } = this.refs;
    if (!reviewButtonPrice) return;
    const selected = [this.selection.chain, ...this.selection.extras].filter(Boolean);
    if (selected.length === 0) {
      reviewButtonPrice.hidden = true;
      reviewButtonPrice.textContent = "";
      return;
    }
    const resolved = (await Promise.all(selected.map((item) => this.#resolveSelected(item)))).filter(Boolean);
    const { rate } = this.#stackState();
    const total = resolved.reduce(
      (sum, { product, variant }) => sum + discounted(variant.price, this.#stackRateFor(product, rate)),
      0
    );
    reviewButtonPrice.textContent = formatCurrency(total);
    reviewButtonPrice.hidden = false;
  }

  async #renderReview() {
    const {
      reviewNecklaceTitle,
      reviewNecklaceItems,
      reviewCharmsTitle,
      reviewExtraItems,
      reviewTotal,
      reviewTotalWas,
      addToCartButton,
    } = this.refs.reviewDialog.refs;
    const { rate } = this.#stackState();

    const resolvedChain = this.selection.chain ? await this.#resolveSelected(this.selection.chain) : null;
    const resolvedExtras = (await Promise.all(this.#expandedExtras().map((extra) => this.#resolveSelected(extra)))).filter(
      Boolean
    );

    reviewNecklaceTitle.textContent = `Necklace (${resolvedChain ? 1 : 0})`;
    reviewNecklaceItems.innerHTML = resolvedChain
      ? this.#renderReviewItem(resolvedChain.product, resolvedChain.variant, resolvedChain.metal, resolvedChain.entry, rate)
      : "";

    reviewCharmsTitle.textContent = `Charms (${resolvedExtras.length})`;
    reviewExtraItems.innerHTML = resolvedExtras
      .map(({ product, variant, metal, entry }) => this.#renderReviewItem(product, variant, metal, entry, rate))
      .join("");

    const all = [...(resolvedChain ? [resolvedChain] : []), ...resolvedExtras];
    const originalTotal = all.reduce((sum, { variant }) => sum + variant.price, 0);
    const total = all.reduce(
      (sum, { product, variant }) => sum + discounted(variant.price, this.#stackRateFor(product, rate)),
      0
    );
    // reviewTotal is a `ref="reviewTotal[]"` array — per Figma, the total
    // shows twice: as its own "Total Price" heading at desktop, and folded
    // into the Add to Cart button's own label at mobile (see
    // .charm-builder__add-to-cart-price, assets/charm-builder.css). Each
    // has a `reviewTotalWas[]` beside it for the pre-discount total.
    const formattedTotal = formatCurrency(total);
    for (const el of reviewTotal) el.textContent = formattedTotal;
    for (const el of reviewTotalWas ?? []) {
      el.hidden = total === originalTotal;
      el.textContent = total === originalTotal ? "" : formatCurrency(originalTotal);
    }

    const ready = Boolean(resolvedChain) && resolvedExtras.length >= this.#extrasLimits().min;
    addToCartButton.setAttribute("aria-disabled", String(!ready));
  }

  /**
   * @param {any} product
   * @param {any} variant - the specific variant selected/resolved for
   *   this item, per #resolveSelected — its own price/image, matching
   *   what was actually added to the grid selection.
   * @param {'gold' | 'silver' | null} metal - the metal this item was
   *   selected under (null for a chain, which has no metal toggle — see
   *   spec §5). Per Figma (node 13240:10015), each card shows a middle
   *   line under the name: the metal for a droplet/charm, or the
   *   variant's own title for a chain (e.g. "Gold / 16"") when it isn't
   *   just "Default Title".
   * @param {CatalogEntry} [entry]
   * @param {number} [rate] - Stack & Save rate earned by the whole
   *   selection; the item shows old + new price when it applies to it
   */
  #renderReviewItem(product, variant, metal, entry, rate = 0) {
    const image = this.#resolveImage(product, variant, entry);
    const metalLabel = metal ? metal.charAt(0).toUpperCase() + metal.slice(1) : "";
    // Chains carry a Length option and are picked per length, so the
    // review line names both ("Gold · 17.5\"") — the length is what
    // distinguishes two otherwise identical chain cards.
    const length = displayLength(this.#variantOptionValue(product, variant, "Length"));
    const meta = [metalLabel, length, entry?.size_label].filter(Boolean).join(" · ") || (variant.title !== "Default Title" ? variant.title : "");
    return `
      <div class="charm-builder__review-item">
        <span class="charm-builder__review-item-card">
          <img class="charm-builder__review-item-image" src="${image}" alt="${escapeHtml(displayTitle(product.title))}" loading="lazy">
        </span>
        <span class="charm-builder__review-item-info">
          <span class="charm-builder__review-item-name">${escapeHtml(displayTitle(product.title))}</span>
          ${meta ? `<span class="charm-builder__review-item-meta">${escapeHtml(meta)}</span>` : ""}
          <span class="charm-builder__review-item-price">${priceHtml(variant.price, this.#stackRateFor(product, rate))}</span>
        </span>
      </div>
    `;
  }
}

if (!customElements.get("charm-builder")) {
  customElements.define("charm-builder", CharmBuilder);
}
